// utils/azureSpeechEvaluator.js
// Phase 1: Azure Speech評価器（完全最終版）

import { supabase } from '../supabaseClient.js'

// ==== 辞書（dictionary）からURLを引く ====
// このファイル内のどこからでも使えるように、クラスの外に定義しておく
const DICT_TABLE = 'dictionary'

/** 単語っぽい1語に正規化（前後の引用符や記号を除去） */
export function normalizeSingleWord(text) {
  if (!text) return ''
  const trimmed = text.trim().toLowerCase().replace(/^["']+|["']+$/g, '')
  // 1語（英字とアポストロフィのみ）に限定。文や複数語なら空にする。
  if (!/^[a-z]+(?:'[a-z]+)?$/.test(trimmed)) return ''
  return trimmed
}

/** Supabaseのdictionaryから url を1件取得。なければ null を返す */
export async function getDictionaryAudioUrl(word) {
  if (!word) return null
  try {
    const { data, error } = await supabase
      .from(DICT_TABLE)
      .select('url')
      .eq('word', word.toLowerCase())   // word は小文字で保存されている（CHECK制約あり）
      .maybeSingle()

    if (error) {
      console.warn('dictionary lookup error:', error.message)
      return null
    }
    return data?.url || null
  } catch (e) {
    console.warn('dictionary fetch failed:', e?.message || e)
    return null
  }
}

// ==== 音声評価ユーティリティ関数（新規追加） ====

/**
 * Pronunciation Assessment を recognizer に適用
 * @param {Object} recognizer - SpeechRecognizerインスタンス
 * @param {string} referenceText - 参照テキスト
 * @param {Object} opts - オプション
 * @param {string} opts.granularity - 粒度（デフォルト: 'Phoneme'）
 * @param {boolean} opts.enableProsody - Prosody評価（デフォルト: false、コスト削減）
 */
export async function applyPronAssessment(recognizer, referenceText, { granularity='Phoneme', enableProsody=false } = {}) {
  const SDK = await loadSpeechSDK()
  const pa = new SDK.PronunciationAssessmentConfig(
    referenceText,
    SDK.PronunciationAssessmentGradingSystem.HundredMark,
    SDK.PronunciationAssessmentGranularity[granularity] ?? SDK.PronunciationAssessmentGranularity.Phoneme,
    // true  // Miscue detection を有効化（以前の設定）
    false   // Miscue detection を無効化（復活させる場合は true に戻す）
  )
  pa.enableProsodyAssessment = !!enableProsody  // 既定でfalse（コスト削減）
  pa.phonemeAlphabet = 'IPA'
  pa.applyTo(recognizer)
}

/**
 * 認識を開始して recognizer を"先に"返す。結果は wait() で待つ方式
 * - 停止ボタンから即座に認識器を停止できるようにするため
 * - STSトークン（authToken）とサブスクキー（key）の両対応
 * 
 * @param {Object} options - 設定オブジェクト
 * @param {string} options.authToken - STSトークン（STSトークンを使う場合）
 * @param {string} options.key - Azure Speech Key（サブスクキーを使う場合）
 * @param {string} options.region - Azure Region
 * @param {string} options.locale - ロケール（例: 'en-US'）
 * @param {string} options.referenceText - 参照テキスト
 * @param {boolean} options.preconnect - 事前接続（デフォルト: true）
 * @returns {Promise<{recognizer: Object, wait: Function, close: Function}>} 認識器とwait/close関数
 */
export async function startPronOnceWithMic({
  authToken,          // STSトークンを使う場合
  key,                // サブスクキーを使う場合
  region,
  locale,
  referenceText,
  preconnect = true,
}) {
  const SDK = await loadSpeechSDK()
  const speechConfig = authToken
    ? SDK.SpeechConfig.fromAuthorizationToken(authToken, region)
    : SDK.SpeechConfig.fromSubscription(key, region)

  speechConfig.speechRecognitionLanguage = locale
  speechConfig.outputFormat = SDK.OutputFormat.Detailed  // ★ NBest/Phonemesを安定化

  const audioCfg = SDK.AudioConfig.fromDefaultMicrophoneInput()
  const recognizer = new SDK.SpeechRecognizer(speechConfig, audioCfg)
  await applyPronAssessment(recognizer, referenceText, { granularity: 'Phoneme', enableProsody: false })

  if (preconnect) {
    try {
      // Connection.fromRecognizer の存在チェック
      if (SDK.Connection && typeof SDK.Connection.fromRecognizer === 'function') {
        const conn = SDK.Connection.fromRecognizer(recognizer)
        
        // openConnection の存在チェック（openConnectionAsync ではなく同期メソッド）
        if (conn && typeof conn.openConnection === 'function') {
          try {
            conn.openConnection()
            console.log('✅ Azure接続（preconnect）成功')
          } catch (e) {
            // preconnect失敗は非致命的（recognizeOnceAsyncで自動接続される）
            console.warn('⚠️ Azure接続（preconnect）失敗（非致命）:', e?.message || e)
          }
        } else {
          console.debug('ℹ️ openConnection メソッドが利用できません。preconnectをスキップします。')
        }
      } else {
        console.debug('ℹ️ Connection.fromRecognizer が利用できません。preconnectをスキップします。')
      }
    } catch (error) {
      // 予期しないエラーでも非致命的として扱う
      console.warn('⚠️ preconnect処理でエラーが発生しました（非致命）:', error?.message || error)
      // throwしない（preconnectは最適化のためのもの）
    }
  }

  const wait = () => new Promise((resolve, reject) => {
    const debugEnabled = typeof window !== 'undefined' && window.AZURE_DEBUG

    if (debugEnabled) {
      recognizer.sessionStarted = (_s, e) => {
        console.log('🟢 [Azure SDK] sessionStarted:', {
          sessionId: e.sessionId,
          referenceText,
          locale
        })
      }
      recognizer.sessionStopped = (_s, e) => {
        console.log('🔴 [Azure SDK] sessionStopped:', {
          sessionId: e.sessionId
        })
      }
      recognizer.speechStartDetected = (_s, e) => {
        console.log('🎙️ [Azure SDK] speechStartDetected:', {
          offset: e.offset
        })
      }
      recognizer.speechEndDetected = (_s, e) => {
        console.log('🛑 [Azure SDK] speechEndDetected:', {
          offset: e.offset
        })
      }
      recognizer.recognizing = (_s, e) => {
        console.log('📝 [Azure SDK] recognizing:', {
          reason: e.result?.reason,
          text: e.result?.text
        })
      }
      recognizer.recognized = (_s, e) => {
        console.log('✅ [Azure SDK] recognized:', {
          reason: e.result?.reason,
          text: e.result?.text
        })
      }
      recognizer.canceled = (_s, e) => {
        console.warn('⚠️ [Azure SDK] canceled:', {
          reason: e.reason,
          errorDetails: e.errorDetails
        })
      }
    }

    recognizer.recognizeOnceAsync(resolve, reject)
  })
  const close = () => {
    try {
      // preconnect で開いたコネクションを明示的に閉じる（best-effort）
      if (preconnect) {
        try {
          if (SDK.Connection && typeof SDK.Connection.fromRecognizer === 'function') {
            const conn = SDK.Connection.fromRecognizer(recognizer)
            if (conn && typeof conn.closeConnection === 'function') {
              conn.closeConnection()
            }
          }
        } catch (e) {
          // closeConnection失敗は無視（best-effort）
          console.debug('⚠️ closeConnection失敗（無視）:', e?.message || e)
        }
      }
      recognizer.close()
    } catch (e) {
      // recognizer.close() 失敗も無視（多重呼び出し対策）
      console.debug('⚠️ recognizer.close()失敗（無視）:', e?.message || e)
    }
  }

  return { recognizer, wait, close }
}

/**
 * マイク直結の1回認識（Azureのエンドポイント検出に依存）
 * - サンプル再生中は呼ばないこと（=送らない）
 * - 認識開始＝試行開始、結果返却＝試行終了
 * - 停止時は呼び出し側で`rec.close()`を確実に呼ぶこと
 * - 後方互換のため維持。新規実装は`startPronOnceWithMic()`を使用推奨
 * 
 * @param {Object} options - 設定オブジェクト
 * @param {string} options.key - Azure Speech Key（本番ではSTSトークンに移行推奨）
 * @param {string} options.region - Azure Region
 * @param {string} options.locale - ロケール（例: 'en-US'）
 * @param {string} options.referenceText - 参照テキスト
 * @param {boolean} options.preconnect - 事前接続（デフォルト: true）
 * @returns {Promise<{result: Object, recognizer: Object}>} SpeechRecognitionResultとrecognizer
 */
export async function recognizePronOnceWithMic(opts) {
  const { recognizer, wait } = await startPronOnceWithMic(opts)
  const result = await wait()
  return { recognizer, result }
}

/**
 * SpeechRecognitionResultからOverallScoreを抽出（共通ヘルパー）
 * @param {Object} result - SpeechRecognitionResult
 * @returns {Promise<number|null>} OverallScore（HundredMark）
 */
export async function extractHundredMarkScore(result) {
  try {
    // 正式APIを使用（非公開プロパティ依存を避ける）
    const SDK = await loadSpeechSDK()
    const rawJson = result?.properties?.getProperty?.(
      SDK.PropertyId.SpeechServiceResponse_JsonResult
    )
    
    // フォールバック: 非公開プロパティ（後方互換性のため）
    const jsonStr = rawJson || result?.privJson || result?.json || '{}'
    const j = typeof jsonStr === 'string' ? JSON.parse(jsonStr) : (jsonStr || {})
    
    return j?.NBest?.[0]?.PronunciationAssessment?.OverallScore
        ?? j?.PronunciationAssessment?.OverallScore ?? null
  } catch {
    return null
  }
}

// Azure Speech SDK 動的インポート（NPMパッケージのES modules形式）
let _sdkPromise = null

export async function loadSpeechSDK() {
  if (_sdkPromise) return _sdkPromise
  
  _sdkPromise = (async () => {
    try {
      // Azure Speech SDK を動的インポート（遅延読み込み）
      const mod = await import('microsoft-cognitiveservices-speech-sdk')
      
      // モジュールのエクスポートを取得（default または名前付き）
      // Azure SDK は { SpeechConfig, SpeechRecognizer, ... } を直接エクスポート
      if (mod.SpeechConfig) {
        return mod
      }
      
      // フォールバック: default エクスポート
      const sdk = mod.default || mod
      
      if (!sdk) {
        throw new Error('SpeechSDK module not found')
      }
      
      return sdk
    } catch (error) {
      console.error('SpeechSDK 読み込みエラー:', error)
      throw error
    }
  })()
  
  return _sdkPromise
}

export class AzureSpeechEvaluator {
  constructor() {
    this.mode = null
    this.speechConfig = null
    this.recognizer = null
    this.isEvaluating = false
    this.onRealtimeText = null // リアルタイムテキストコールバック
    
    // トークン管理
    this.currentToken = null
    this.tokenExpiry = null
    this.tokenRefreshTimer = null
    
    // 音声ストリーム管理（MediaRecorder用）
    this.audioStream = null
    this.recordingPromise = null
    this.mediaRecorder = null
    this.recordingChunks = []
    
    // PushAudioInputStream関連（継続セッション用）
    this.pushAudioStream = null
    this.audioContext = null
    this.scriptProcessor = null
    this.sourceNode = null
    this.silencePushTimer = null
    this.isPlaying = false  // 参照音再生中フラグ
    this.inputSampleRate = 44100  // デフォルト（実際の値はマイク取得時に設定）
    
    // 継続セッション用コールバック
    this.onSessionRecognized = null
    this.onVoiceActivity = null
    
    // 現在の参照テキスト（クロージャ問題対策）
    this.currentReferenceText = ''
    
    // 参照音声再生用のAudio要素（iOS対策）
    this._refAudio = null
    this._iosUnlocked = false
    
    // Prosody評価の有効/無効フラグ（コスト削減のため無効化）
    // 将来的に復活する可能性があるため、コードはコメントアウトで保持
    // falseにするとProsody評価をスキップ（コスト削減、レイテンシには影響なし）
    this.enableProsody = false  // コスト削減のため無効化（追加料金がかかるため）
    
    // レイテンシ計測用
    this.latencyMetrics = {
      sdkResponseStart: null,  // recognizedイベント発火時
      parseStart: null,         // parseSDKResult開始時
      parseEnd: null,           // parseSDKResult終了時
      prosodyParseStart: null,  // parseProsodyAnalysis開始時
      prosodyParseEnd: null     // parseProsodyAnalysis終了時
    }
    
    // ブラウザ検出を遅延実行（import.meta.envが利用可能になるまで待つ）
    this.initializeMode()
  }
  
  /**
   * 評価モードを初期化
   */
  async initializeMode() {
    try {
      // 動的インポートでBrowserDetectorを読み込み
      const { BrowserDetector } = await import('./browserDetection.js')
      
      // デバッグ情報を出力
      BrowserDetector.logDeviceInfo()
      
      this.mode = BrowserDetector.detectEvalMode()
      console.log('🎤 Azure評価モード:', this.mode)
      
      // SDKモードの場合、SDK読み込みテスト
      if (this.mode === 'SDK') {
        try {
          const speechsdk = await loadSpeechSDK()
          console.log('✅ Azure Speech SDK読み込み成功:', !!speechsdk)
        } catch (sdkError) {
          console.warn('⚠️ SDK読み込み失敗、RESTモードにフォールバック:', sdkError)
          this.mode = 'REST'
        }
      }
      
    } catch (error) {
      console.warn('ブラウザ検出失敗、RESTモードにフォールバック:', error)
      this.mode = 'REST'
    }
  }
  
  /**
   * トークン取得・更新（自動ローテーション対応）
   */
  async ensureValidToken() {
    const now = Date.now()
    
    // トークンが有効で、残り時間が2分以上ある場合はそのまま使用
    if (this.currentToken && this.tokenExpiry && (this.tokenExpiry - now) > 120000) {
      return this.currentToken
    }
    
    console.log('🔄 Azureトークン更新中...')
    
    // 新しいトークンを取得
    const session = await supabase.auth.getSession()
    if (!session?.data?.session?.access_token) {
      const err = new Error('未ログインのためAzureトークンを取得できません')
      err.code = 'NO_AUTH_SESSION'
      throw err
    }
    
    const { getApiBase } = await import('./apiBase.js')
    const apiBase = getApiBase()
    const tokenResponse = await fetch(`${apiBase}/api/azure/token`, {
      headers: {
        'Authorization': `Bearer ${session.data.session.access_token}`
      }
    })
    
    if (!tokenResponse.ok) {
      const errorText = await tokenResponse.text().catch(() => '')
      let errorMessage = 'Azureトークン取得に失敗しました'
      
      if (tokenResponse.status === 401 || tokenResponse.status === 403) {
        errorMessage = '認証に失敗しました。ログインし直してください。'
        const err = new Error(errorMessage)
        err.code = 'AUTH_FAILED'
        err.errorType = 'AUTH_ERROR'
        err.status = tokenResponse.status
        throw err
      } else if (tokenResponse.status === 500 || tokenResponse.status === 502 || tokenResponse.status === 503) {
        errorMessage = 'サーバーエラーが発生しました。しばらく待ってから再度お試しください。'
        const err = new Error(errorMessage)
        err.code = 'SERVER_ERROR'
        err.errorType = 'TOKEN_ERROR'
        err.status = tokenResponse.status
        throw err
      } else {
        const err = new Error(`${errorMessage} (ステータス: ${tokenResponse.status})`)
        err.code = 'TOKEN_FETCH_ERROR'
        err.errorType = 'TOKEN_ERROR'
        err.status = tokenResponse.status
        err.responseText = errorText
        throw err
      }
    }
    
    const { token, region, expires_in } = await tokenResponse.json()
    
    // トークン情報を保存
    this.currentToken = { token, region }
    this.tokenExpiry = now + (expires_in * 1000) - 60000 // 1分前を安全マージン
    
    // 次回更新タイマー設定（8分後）
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer)
    }
    this.tokenRefreshTimer = setTimeout(async () => {
      try {
        await this.ensureValidToken() // バックグラウンド更新
      } catch (e) {
        console.warn('Token refresh failed (will retry on next call):', e?.message || e)
      }
    }, 8 * 60 * 1000)
    
    console.log('✅ Azureトークン更新完了:', {
      expires_in: `${expires_in}秒`,
      next_refresh: '8分後'
    })
    
    return this.currentToken
  }

  /**
   * リアルタイム評価開始（Azure公式仕様準拠）
   */
  async startRealtimeEvaluation(text, onRealtimeText = null) {
    if (this.isEvaluating) {
      throw new Error('評価中です。しばらくお待ちください')
    }
    
    this.isEvaluating = true
    
    try {
      // SDK をグローバル方式でロード
      const speechsdk = await loadSpeechSDK()
      
      // 有効なトークンを確保（自動更新）
      const { token, region } = await this.ensureValidToken()
      
      // トークンで初期化（キー露出を防止）
      this.speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(token, region)
      
      this.speechConfig.speechRecognitionLanguage = 'en-US'
      // Detailed出力形式を指定（ProsodyAssessmentを含む詳細JSONを取得）
      this.speechConfig.outputFormat = speechsdk.OutputFormat.Detailed
      
      // 音声認識の精度向上設定
      this.speechConfig.setProperty(speechsdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, '5000')
      this.speechConfig.setProperty(speechsdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, '2000')
      // AudioLoggingは本番ではOFF推奨（コスト・プライバシー・レイテンシ削減のため）
      // デバッグ時のみ有効化: this.speechConfig.setProperty(speechsdk.PropertyId.SpeechServiceConnection_EnableAudioLogging, 'true')
      
      console.log('🔍 Azure Speech設定完了:', {
        language: 'en-US',
        region: region,
        silenceTimeout: '5000ms',
        endSilenceTimeout: '2000ms',
        tokenExpiry: new Date(this.tokenExpiry).toLocaleTimeString()
      })
      
      // Pronunciation Assessment設定（音素レベル詳細スコア）
      // 注意：第4引数は enableMiscue（挿入/脱落検出）であり、Prosodyではない
      const pronunciationConfig = new speechsdk.PronunciationAssessmentConfig(
        text,
        speechsdk.PronunciationAssessmentGradingSystem.HundredMark,
        speechsdk.PronunciationAssessmentGranularity.Phoneme // Word → Phoneme
      )
      
      // Prosody評価設定（コスト削減のため無効化中、将来的に復活可能）
      // Prosody評価は追加料金がかかるため、現在は無効化
      /*
      // Prosody評価を明示的に有効化（SDK 1.34.0+、プロパティで設定）
      // Note: Prosodyはen-USロケールのみ対応、Japan Eastでもen-USなら利用可
      try {
        // プロパティとして設定を試みる（フラグで制御可能）
        if ('enableProsodyAssessment' in pronunciationConfig) {
          pronunciationConfig.enableProsodyAssessment = this.enableProsody
        }
        if ('phonemeAlphabet' in pronunciationConfig) {
          pronunciationConfig.phonemeAlphabet = "IPA" // IPA発音記号で返す
        }
        if ('nbestPhonemeCount' in pronunciationConfig) {
          pronunciationConfig.nbestPhonemeCount = 1 // 候補は最小限で十分
        }
      } catch (error) {
        console.warn('⚠️ Prosody設定でエラー:', error)
      }
      
      // 設定の確認
      console.log('✅ Prosody評価を有効化しました', {
        enableProsodyAssessment: pronunciationConfig.enableProsodyAssessment,
        phonemeAlphabet: pronunciationConfig.phonemeAlphabet,
        nbestPhonemeCount: pronunciationConfig.nbestPhonemeCount,
        outputFormat: this.speechConfig.outputFormat,
        outputFormatType: typeof this.speechConfig.outputFormat,
        hasEnableProsodyAssessment: 'enableProsodyAssessment' in pronunciationConfig
      })
      */
      
      // IPA発音記号とNBest設定は保持（Prosody評価とは無関係）
      try {
        if ('phonemeAlphabet' in pronunciationConfig) {
          pronunciationConfig.phonemeAlphabet = "IPA" // IPA発音記号で返す
        }
        if ('nbestPhonemeCount' in pronunciationConfig) {
          pronunciationConfig.nbestPhonemeCount = 1 // 候補は最小限で十分
        }
      } catch (error) {
        console.warn('⚠️ 設定でエラー:', error)
      }
      
      // 1回だけマイク取得（SDKとMediaRecorderで共有）
      if (!this.audioStream) {
        this.audioStream = await navigator.mediaDevices.getUserMedia({ 
          audio: { 
            echoCancellation: false, // SDKで処理するため無効化
            noiseSuppression: false,
            autoGainControl: false
          } 
        })
      }
      
      // MediaRecorder初期化（100点時の音声保存用）
      this.recordingChunks = []
      // iOS/Safari対策: audio/mp4 を優先
      const mimeType = /iphone|ipad|ipod|safari/i.test(navigator.userAgent) && MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/webm'
      
      this.mediaRecorder = new MediaRecorder(this.audioStream, { mimeType })
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordingChunks.push(event.data)
        }
      }
      
      // 録音開始（評価中に並行録音）
      this.mediaRecorder.start()
      console.log('🎤 MediaRecorder録音開始（100点時保存用）:', { mimeType })
      
      // SDK用: ブラウザのMediaStreamを直接渡すのは非推奨/非互換のため、SDKのデフォルトマイクを使用
      // MediaRecorderは別途this.audioStreamで録音（保存用途）
      const audioConfig = speechsdk.AudioConfig.fromDefaultMicrophoneInput()
      this.recognizer = new speechsdk.SpeechRecognizer(this.speechConfig, audioConfig)
      
      // PronunciationAssessmentConfigをrecognizerに適用
      pronunciationConfig.applyTo(this.recognizer)
      if (onReady) {
        try {
          onReady()
        } catch (callbackError) {
          console.warn('⚠️ onReadyコールバックでエラー:', callbackError)
        }
      }
      
      // 適用後の確認（Prosody設定は無効化中）
      console.log('🔍 PronunciationConfig適用完了')
      
      console.log('✅ SDK初期化完了（SDKデフォルトマイク使用）')
      
      return await new Promise((resolve, reject) => {
        let finalResult = null
        let resolveCalled = false  // 多重 resolve 防止フラグ
        
        // 途中経過イベント（リアルタイムフィードバック）
        this.recognizer.recognizing = (sender, e) => {
          if (e.result.reason === speechsdk.ResultReason.RecognizingSpeech) {
            console.log('🔍 途中経過認識:', e.result.text)
            // UIに途中経過を送信
            if (onRealtimeText) {
              onRealtimeText(e.result.text)
            }
          }
        }
        
        // 最終結果イベント
        this.recognizer.recognized = (sender, e) => {
          try {
            if (resolveCalled) return  // 既に解決済みなら何もしない
            
            console.log('🔍 SDK最終結果:', e.result)
            console.log('🔍 SDK最終結果詳細:', {
              reason: e.result.reason,
              text: e.result.text,
              duration: e.result.duration,
              offset: e.result.offset,
              properties: e.result.properties
            })
            
            // 音声認識の詳細分析
            console.log('🔍 音声認識分析:', {
              認識テキスト: e.result.text,
              期待テキスト: text,
              認識時間: `${e.result.duration / 10000000}秒`,
              音声長さ: e.result.duration,
              認識理由: e.result.reason,
              認識成功: e.result.reason === speechsdk.ResultReason.RecognizedSpeech
            })
            
            if (!e.result || e.result.reason === speechsdk.ResultReason.NoMatch) {
              throw new Error('音声が認識されませんでした。もう一度はっきりと話してください。')
            }
            if (e.result.reason === speechsdk.ResultReason.Canceled) {
              throw new Error('音声認識がキャンセルされました')
            }
            
            // 認識テキストの検証
            const recognizedText = e.result.text?.trim() || ''
            
            // 実質的に無音の場合（ピリオドや記号のみ）
            if (recognizedText === '.' || recognizedText === '...' || recognizedText === '') {
              console.warn('⚠️ 音声が検出されませんでした（無音）:', recognizedText)
              throw new Error('音声が検出されませんでした。もう一度はっきりと話してください。')
            }
            
            // テキストが短すぎる場合
            if (recognizedText.length < 3) {
              console.warn('⚠️ 認識されたテキストが短すぎます:', recognizedText)
              throw new Error('音声が短すぎます。もう一度はっきりと話してください。')
            }
            
            // 生JSONを取得（ProsodyAssessmentの確認用）
            let rawJsonData = null
            try {
              const rawJson = e.result.properties.getProperty(
                speechsdk.PropertyId.SpeechServiceResponse_JsonResult
              )
              if (rawJson) {
                rawJsonData = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson
                console.log('🔍 RAW JSON (サービス応答):', rawJsonData)
                console.log('🔍 RAW JSON全キー:', Object.keys(rawJsonData))
                
                // ProsodyAssessment確認（コスト削減のため無効化中）
                /*
                // NBest配列の確認
                if (rawJsonData.NBest && Array.isArray(rawJsonData.NBest) && rawJsonData.NBest.length > 0) {
                  console.log('🔍 NBest[0]全キー:', Object.keys(rawJsonData.NBest[0]))
                  if (rawJsonData.NBest[0].ProsodyAssessment) {
                    console.log('✅ ProsodyAssessmentをNBest[0]で発見:', rawJsonData.NBest[0].ProsodyAssessment)
                  }
                }
                
                // トップレベルのProsodyAssessment確認
                if (rawJsonData.ProsodyAssessment) {
                  console.log('✅ ProsodyAssessmentをトップレベルで発見:', rawJsonData.ProsodyAssessment)
                }
                */
              } else {
                console.warn('⚠️ RAW JSONが取得できませんでした')
              }
            } catch (error) {
              console.warn('⚠️ RAW JSONパースエラー:', error)
            }
            
            const pronunciationResult = speechsdk.PronunciationAssessmentResult.fromResult(e.result)
            console.log('🔍 PronunciationAssessmentResult:', pronunciationResult)
            // RAW JSONデータをparseSDKResultに渡す（ProsodyAssessment取得のため）
            finalResult = this.parseSDKResult(pronunciationResult, text, rawJsonData)
            
            // 認識完了（継続認識は停止しない）
            if (!resolveCalled) {
              resolveCalled = true
              resolve(finalResult)
            }
            
          } catch (error) {
            console.error('SDK結果パースエラー:', error)
            if (!resolveCalled) {
              resolveCalled = true
              reject(error)
            }
          }
        }
        
        // エラーイベント
        this.recognizer.canceled = (sender, e) => {
          console.error('SDK認識キャンセル:', e.reason, e.errorDetails)
          if (!resolveCalled) {
            resolveCalled = true
            reject(new Error(`認識がキャンセルされました: ${e.errorDetails}`))
          }
        }
        
        // 録音停止時の処理（外部から呼ばれる）
        this.stopAndResolve = async () => {
          if (resolveCalled) return
          resolveCalled = true
          
          try {
            // MediaRecorder停止と録音データ取得
            const audioBlob = await this.stopRecordingAndGetBlob()
            if (finalResult) {
              finalResult.audioBlob = audioBlob
              resolve(finalResult)
            } else {
              // 結果がない場合はnullを返す
              resolve(null)
            }
          } catch (error) {
            if (!resolveCalled) {
              resolveCalled = true
              reject(error)
            }
            console.warn('⚠️ 録音データ取得エラー:', error)
            resolve(finalResult || null)
          }
        }
        
        // リアルタイム認識開始（継続モード）
        this.recognizer.startContinuousRecognitionAsync()
      }).finally(() => {
        this.isEvaluating = false
      })
    } catch (error) {
      // Promise作成前にエラーが発生した場合
      this.isEvaluating = false
      throw error
    }
  }
  
  /**
   * 評価停止（録音も停止してAPI切断）
   */
  async stopRealtimeEvaluation() {
    // stopAndResolveが設定されている場合は呼び出す（evaluateWithSDKのPromiseを解決）
    if (this.stopAndResolve) {
      await this.stopAndResolve()
      this.stopAndResolve = null
    }
    
    if (this.recognizer) {
      this.recognizer.stopContinuousRecognitionAsync()
      this.recognizer = null
    }
    
    // MediaRecorderも停止
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop()
      } catch (error) {
        console.warn('⚠️ MediaRecorder停止エラー:', error)
      }
      this.mediaRecorder = null
    }
    this.recordingChunks = []
    
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop())
      this.audioStream = null
    }
    
    this.isEvaluating = false
  }
  
  /**
   * MediaRecorder用音声ストリーム取得
   */
  getAudioStream() {
    return this.audioStream
  }
  
  /**
   * Float32 → Int16 PCM変換
   * @param {Float32Array} float32Array - 入力（Float32、-1.0～1.0）
   * @returns {Uint8Array} - 出力（16bit PCM、リトルエンディアン）
   */
  floatTo16PCM(float32Array) {
    const int16Array = new Int16Array(float32Array.length)
    for (let i = 0; i < float32Array.length; i++) {
      const sample = Math.max(-1, Math.min(1, float32Array[i]))
      int16Array[i] = sample < 0 
        ? sample * 0x8000 
        : sample * 0x7FFF
    }
    return new Uint8Array(int16Array.buffer)
  }
  
  /**
   * ダウンサンプリング（任意のサンプルレート → 16kHz）
   * @param {Float32Array} float32Array - 入力
   * @param {number} inputSampleRate - 入力サンプルレート
   * @returns {Float32Array} - 16kHzダウンサンプル済み
   */
  downsampleTo16k(float32Array, inputSampleRate) {
    const targetSampleRate = 16000
    if (inputSampleRate === targetSampleRate) {
      return float32Array
    }
    
    const ratio = inputSampleRate / targetSampleRate
    const newLength = Math.floor(float32Array.length / ratio)
    const result = new Float32Array(newLength)
    
    // 線形補間
    for (let i = 0; i < newLength; i++) {
      const srcIndex = i * ratio
      const srcIndexFloor = Math.floor(srcIndex)
      const srcIndexCeil = Math.min(srcIndexFloor + 1, float32Array.length - 1)
      const fraction = srcIndex - srcIndexFloor
      
      result[i] = float32Array[srcIndexFloor] * (1 - fraction) + 
                   float32Array[srcIndexCeil] * fraction
    }
    
    return result
  }
  
  /**
   * RMS（Root Mean Square）計算（無音検知用）
   * @param {Float32Array} float32Array - 入力
   * @returns {number} - RMS値
   */
  calculateRMS(float32Array) {
    let sum = 0
    for (let i = 0; i < float32Array.length; i++) {
      sum += float32Array[i] * float32Array[i]
    }
    return Math.sqrt(sum / float32Array.length)
  }
  
  /**
   * MediaRecorder停止して録音Blobを取得
   * @returns {Promise<Blob|null>} 録音データ（失敗時はnull）
   */
  stopRecordingAndGetBlob() {
    return new Promise((resolve) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        resolve(null)
        return
      }
      
      try {
        this.mediaRecorder.onstop = () => {
          try {
            if (this.recordingChunks.length > 0) {
              const blob = new Blob(this.recordingChunks, { 
                type: this.mediaRecorder.mimeType || 'audio/webm' 
              })
              console.log('✅ 録音データ取得完了:', { size: blob.size, type: blob.type })
              this.recordingChunks = []
              this.mediaRecorder = null
              resolve(blob)
            } else {
              console.warn('⚠️ 録音データが空です')
              resolve(null)
            }
          } catch (error) {
            console.error('⚠️ 録音Blob生成エラー:', error)
            resolve(null)
          }
        }
        
        // 録音停止
        if (this.mediaRecorder.state === 'recording') {
          this.mediaRecorder.stop()
        } else {
          resolve(null)
        }
      } catch (error) {
        console.error('⚠️ MediaRecorder停止エラー:', error)
        this.mediaRecorder = null
        this.recordingChunks = []
        resolve(null)
      }
    })
  }

  /**
   * SDK評価（マイク直接入力）
   * @param {string} text - 評価対象テキスト
   * @param {Function} onRealtimeText - 途中経過テキストコールバック
   * @param {Function} onRecognized - 認識完了時のコールバック（スコア更新用、録音は継続）
   */
  async evaluateWithSDK(text, onRealtimeText = null, onRecognized = null, options = {}) {
    if (this.isEvaluating) {
      throw new Error('評価中です。しばらくお待ちください')
    }
  
    this.isEvaluating = true
    const onReady = typeof options?.onReady === 'function' ? options.onReady : null
  
    try {
      const speechsdk = await loadSpeechSDK()
  
      // --- ❶ まず Sticky recognizer を最優先で試す ---
      let usingSticky = false
      try {
        const sticky = await getOrCreateStickyRecognizer({ language: 'en-US' })
        if (sticky) {
          this.recognizer = sticky
          usingSticky = true
          console.log('🟢 Using STICKY recognizer')
        }
      } catch (e) {
        console.warn('↩️ Sticky利用不可 → 通常経路にフォールバック:', e?.message || e)
      }
  
      // --- ❷ Stickyが使えない場合は従来どおり新規 recognizer を作成 ---
      if (!usingSticky) {
        // 有効なトークンを確保（自動更新）
        const { token, region } = await this.ensureValidToken()
  
        // トークンで初期化（キー露出防止）
        this.speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(token, region)
        this.speechConfig.speechRecognitionLanguage = 'en-US'
        this.speechConfig.outputFormat = speechsdk.OutputFormat.Detailed
        // 無音タイムアウト（Stickyユーティリティ側とも値を揃える）
        this.speechConfig.setProperty(
          speechsdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, '5000'
        )
        this.speechConfig.setProperty(
          speechsdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, '2000'
        )
  
        console.log('🔍 Azure Speech設定完了:', {
          language: 'en-US',
          region,
          silenceTimeout: '5000ms',
          endSilenceTimeout: '2000ms',
          tokenExpiry: new Date(this.tokenExpiry).toLocaleTimeString()
        })
  
        const audioConfig = speechsdk.AudioConfig.fromDefaultMicrophoneInput()
        this.recognizer = new speechsdk.SpeechRecognizer(this.speechConfig, audioConfig)
        console.log('🔵 Using NON-STICKY recognizer（新規作成）')
      }
  
      // --- ❸ マイク取得（MediaRecorderは保存用。Sticky/非Stickyに関わらず併用） ---
      if (!this.audioStream) {
        try {
          this.audioStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
          })
        } catch (error) {
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
            const e = new Error('マイクの使用が許可されていません。ブラウザの設定でマイクの許可をお願いします。')
            e.code = 'MICROPHONE_PERMISSION_DENIED'
            e.type = 'permission'
            throw e
          }
          throw error
        }
      }
  
      // MediaRecorder（100点時の保存用）
      this.recordingChunks = []
      const mimeType =
        /iphone|ipad|ipod|safari/i.test(navigator.userAgent) && MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
            : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm'))
      this.mediaRecorder = new MediaRecorder(this.audioStream, { mimeType })
      this.mediaRecorder.ondataavailable = e => { if (e.data.size > 0) this.recordingChunks.push(e.data) }
      this.mediaRecorder.start()
      console.log('🎤 MediaRecorder録音開始（100点時保存用）:', { mimeType })
  
      // --- ❹ Pronunciation Assessment を毎回適用（テキストごとに更新必要） ---
      const pronunciationConfig = new speechsdk.PronunciationAssessmentConfig(
        text,
        speechsdk.PronunciationAssessmentGradingSystem.HundredMark,
        speechsdk.PronunciationAssessmentGranularity.Phoneme
      )
      try {
        if ('enableProsodyAssessment' in pronunciationConfig) {
          pronunciationConfig.enableProsodyAssessment = this.enableProsody // フラグでON/OFF
        }
        if ('phonemeAlphabet' in pronunciationConfig) pronunciationConfig.phonemeAlphabet = 'IPA'
        if ('nbestPhonemeCount' in pronunciationConfig) pronunciationConfig.nbestPhonemeCount = 1
      } catch (err) {
        console.warn('⚠️ Prosody/IPA 設定でエラー:', err)
      }
  
      // Sticky/非Sticky どちらにも適用（Stickyの “使い回し” でも毎回上書き適用）
      pronunciationConfig.applyTo(this.recognizer)
      if (typeof onReady === 'function') {
        try {
          onReady()
        } catch (callbackError) {
          console.warn('⚠️ onReadyコールバックでエラー:', callbackError)
        }
      }
      console.log('🔍 PronunciationConfig適用完了')
  
      // --- ❺ イベント購読と開始（共通） ---
      return await new Promise((resolve, reject) => {
        this.recognizer.recognizing = (_sender, e) => {
          if (e.result.reason === speechsdk.ResultReason.RecognizingSpeech) {
            console.log('🔍 途中経過認識:', e.result.text)
            onRealtimeText?.(e.result.text)
          }
        }
  
        this.recognizer.recognized = (_sender, e) => {
          try {
            this.latencyMetrics.sdkResponseStart = performance.now()
  
            console.log('🔍 SDK認識結果:', e.result)
            console.log('🔍 SDK認識結果詳細:', {
              reason: e.result.reason,
              text: e.result.text,
              duration: e.result.duration,
              offset: e.result.offset,
              properties: e.result.properties
            })
  
            if (!e.result || e.result.reason === speechsdk.ResultReason.Canceled) return
            if (e.result.reason === speechsdk.ResultReason.NoMatch) return
  
            const recognizedText = e.result.text?.trim() || ''
            if (recognizedText === '.' || recognizedText === '...' || recognizedText === '') {
              console.warn('⚠️ 音声が検出されませんでした（無音）:', recognizedText)
              return
            }
            if (recognizedText.length < 3) {
              console.warn('⚠️ 認識されたテキストが短すぎます:', recognizedText)
              return
            }
  
            let rawJsonData = null
            try {
              const rawJson = e.result.properties.getProperty(
                speechsdk.PropertyId.SpeechServiceResponse_JsonResult
              )
              if (rawJson) rawJsonData = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson
              if (rawJsonData) console.log('🔍 RAW JSON (サービス応答):', rawJsonData)
            } catch (err) {
              console.warn('⚠️ RAW JSONパースエラー:', err)
            }
  
            const pa = speechsdk.PronunciationAssessmentResult.fromResult(e.result)
            const result = this.parseSDKResult(pa, text, rawJsonData)
            onRecognized?.(result)
          } catch (err) {
            console.error('SDK結果パースエラー:', err)
          }
        }
  
        this.recognizer.canceled = (_s, e) => {
          console.error('SDK認識キャンセル:', e.reason, e.errorDetails)
          try { this.recognizer.stopContinuousRecognitionAsync() } catch {}
          let msg = `認識がキャンセルされました: ${e.errorDetails}`
          let code = 'RECOGNITION_CANCELED'
          if (e.errorDetails && /network|connection|timeout/i.test(e.errorDetails)) {
            msg = 'ネットワークエラーが発生しました。接続を確認してください。'
            code = 'NETWORK_ERROR'
          }
          const error = new Error(msg)
          error.code = code
          error.type = 'recognition'
          reject(error)
        }
  
        // 連続認識開始（Sticky/非Sticky共通）
        this.recognizer.startContinuousRecognitionAsync()
      }).finally(() => {
        this.isEvaluating = false
      })
    } catch (error) {
      this.isEvaluating = false
      throw error
    }
  }
  
  
  
  /**
   * AbortSignal.timeout のフォールバック（Safari/一部Android対応）
   */
  _getAbortSignal(ms) {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      return AbortSignal.timeout(ms)
    }
    const ctrl = new AbortController()
    setTimeout(() => ctrl.abort(), ms)
    return ctrl.signal
  }

  /**
   * REST評価（Blob入力）
   */
  async evaluateWithREST(audioBlob, text) {
    if (this.isEvaluating) {
      throw new Error('評価中です。しばらくお待ちください')
    }
    
    this.isEvaluating = true
    
    try {
      // 本番は絶対URL（VITE_API_BASE）、開発は相対パス
      const { getApiBase } = await import('./apiBase.js')
      const apiBase = getApiBase()
      const ct = audioBlob?.type || 'audio/webm' // Content-Typeフォールバック
      const response = await fetch(`${apiBase}/api/azure/pronounce`, {
        method: 'POST',
        headers: {
          'Content-Type': ct,
          'X-Text': encodeURIComponent(text), // フロントでエンコード
          'Accept': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: audioBlob,
        signal: this._getAbortSignal(8000)
      })
      
      if (!response.ok) {
        throw new Error(`評価APIエラー: ${response.status}`)
      }
      
      const result = await response.json()
      return this.parseRESTResult(result)
      
    } finally {
      this.isEvaluating = false
    }
  }
  
  /**
   * 評価実行（メイン関数）
   */
  async evaluate(text, taskId, onRealtimeText = null, audioBlob = null) {
    // モードが未初期化の場合は待機
    if (!this.mode) {
      await this.initializeMode()
    }
    
    // レート制御（task_idベース）
    const rateLimitKey = `rate_limit_${taskId}`
    const lastEval = localStorage.getItem(rateLimitKey)
    const now = Date.now()
    
    if (lastEval && (now - parseInt(lastEval)) < 5000) {
      throw new Error('同じ文の評価は5秒間隔でお願いします')
    }
    
    localStorage.setItem(rateLimitKey, now.toString())
    
    try {
      if (this.mode === 'SDK') {
        // SDK: マイク直接入力で評価
        try {
          return await this.evaluateWithSDK(text, onRealtimeText)
        } catch (sdkError) {
          console.warn('SDK評価失敗、RESTにフォールバック:', sdkError)
          // SDK失敗時はRESTにフォールバック（Blobが必要）
          if (!audioBlob) {
            throw new Error('SDK評価に失敗しました。録音データが必要です。')
          }
          return await this.evaluateWithREST(audioBlob, text)
        }
      } else {
        // REST: Blobで評価（必須）
        if (!audioBlob) {
          throw new Error('REST評価には音声Blobが必要です')
        }
        return await this.evaluateWithREST(audioBlob, text)
      }
    } catch (error) {
      // リトライ1回
      console.warn('評価失敗、リトライ中:', error)
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      if (this.mode === 'SDK') {
        try {
          return await this.evaluateWithSDK(text, onRealtimeText)
        } catch (sdkError) {
          console.warn('SDK再試行失敗:', sdkError)
          if (!audioBlob) {
            throw new Error('SDK評価に失敗しました。録音データが必要です。')
          }
          return await this.evaluateWithREST(audioBlob, text)
        }
      } else {
        if (!audioBlob) {
          throw new Error('REST評価には音声Blobが必要です')
        }
        return await this.evaluateWithREST(audioBlob, text)
      }
    }
  }
  
  /**
   * SDK結果をパース
   * @param {Object} pronunciationResult - Azure Pronunciation Assessment結果
   * @param {string} text - 期待テキスト（参考文）
   * @param {Object} rawJsonData - サービスからの生JSON（ProsodyAssessment取得用）
   */
  parseSDKResult(pronunciationResult, text, rawJsonData = null) {
    // レイテンシ計測開始
    this.latencyMetrics.parseStart = performance.now()
    
    console.log('🔍 SDK結果パース:', pronunciationResult)
    
    // 詳細デバッグ：プロパティを個別に確認
    console.log('🔍 プロパティ詳細:', {
      pronunciationScore: pronunciationResult.pronunciationScore,
      accuracyScore: pronunciationResult.accuracyScore,
      fluencyScore: pronunciationResult.fluencyScore,
      completenessScore: pronunciationResult.completenessScore,
      words: pronunciationResult.words,
      privPronJson: pronunciationResult.privPronJson
    })
    
    // privPronJsonから詳細情報を取得
    let detailedScores = {}
    if (pronunciationResult.privPronJson) {
      console.log('🔍 privPronJson詳細:', pronunciationResult.privPronJson)
      detailedScores = pronunciationResult.privPronJson
      
      // JSON文字列の場合はパース
      if (typeof detailedScores === 'string') {
        try {
          detailedScores = JSON.parse(detailedScores)
          console.log('🔍 privPronJsonパース後:', detailedScores)
        } catch (e) {
          console.warn('⚠️ privPronJsonパース失敗:', e)
        }
      }
      
      // デバッグ：全てのキーを確認
      console.log('🔍 privPronJson全キー:', Object.keys(detailedScores))
      
      // ProsodyAssessment取得処理（コスト削減のため無効化中、将来的に復活可能）
      /*
      // NBest配列がある場合は確認
      if (detailedScores.NBest && Array.isArray(detailedScores.NBest) && detailedScores.NBest.length > 0) {
        console.log('🔍 NBest[0]全キー:', Object.keys(detailedScores.NBest[0]))
        // NBest[0]にProsodyAssessmentがある可能性
        if (detailedScores.NBest[0].ProsodyAssessment || detailedScores.NBest[0].prosodyAssessment) {
          detailedScores.ProsodyAssessment = detailedScores.NBest[0].ProsodyAssessment || detailedScores.NBest[0].prosodyAssessment
          console.log('✅ ProsodyAssessmentをNBest[0]から取得')
        }
      }
      
      // RAW JSONからもProsodyAssessmentを確認（privPronJsonにない場合のフォールバック）
      if (!detailedScores.ProsodyAssessment && rawJsonData) {
        // RAW JSONのNBest[0]から取得
        if (rawJsonData.NBest && Array.isArray(rawJsonData.NBest) && rawJsonData.NBest.length > 0) {
          if (rawJsonData.NBest[0].ProsodyAssessment || rawJsonData.NBest[0].prosodyAssessment) {
            detailedScores.ProsodyAssessment = rawJsonData.NBest[0].ProsodyAssessment || rawJsonData.NBest[0].prosodyAssessment
            console.log('✅ ProsodyAssessmentをRAW JSONのNBest[0]から取得')
          }
        }
        // RAW JSONのトップレベルから取得
        if (!detailedScores.ProsodyAssessment && (rawJsonData.ProsodyAssessment || rawJsonData.prosodyAssessment)) {
          detailedScores.ProsodyAssessment = rawJsonData.ProsodyAssessment || rawJsonData.prosodyAssessment
          console.log('✅ ProsodyAssessmentをRAW JSONのトップレベルから取得')
        }
      }
      */
    }
    
    // 単語データの取得（複数のソースから試行）
    let words = pronunciationResult.words || []
    if (words.length === 0 && detailedScores) {
      // privPronJsonから取得を試みる
      if (detailedScores.Words && Array.isArray(detailedScores.Words)) {
        words = detailedScores.Words
        console.log('✅ privPronJson.Wordsから単語取得:', words.length, '単語')
      } else if (detailedScores.NBest && Array.isArray(detailedScores.NBest) && detailedScores.NBest[0]?.Words) {
        words = detailedScores.NBest[0].Words
        console.log('✅ privPronJson.NBest[0].Wordsから単語取得:', words.length, '単語')
      }
    }
    
    const overall = pronunciationResult.pronunciationScore ?? detailedScores.PronunciationScore ?? null
    const accuracy = pronunciationResult.accuracyScore ?? detailedScores.AccuracyScore ?? null
    const fluency = pronunciationResult.fluencyScore ?? detailedScores.FluencyScore ?? null
    const completeness = pronunciationResult.completenessScore ?? detailedScores.CompletenessScore ?? null
    
    console.log('🔍 最終スコア:', { overall, accuracy, fluency, completeness })
    
    // Prosody分析結果（コスト削減のため無効化中、将来的に復活可能）
    // Prosody評価は追加料金がかかるため、現在は無効化
    let prosodyResult = null
    /*
    console.log('🔍 ProsodyAssessment確認:', detailedScores.ProsodyAssessment)
    if (this.enableProsody) {
      this.latencyMetrics.prosodyParseStart = performance.now()
      prosodyResult = this.parseProsodyAnalysis(detailedScores, words)
      this.latencyMetrics.prosodyParseEnd = performance.now()
      console.log('🔍 Prosody分析結果:', prosodyResult)
      
      // Prosody処理時間をログ出力
      const prosodyParseTime = this.latencyMetrics.prosodyParseEnd - this.latencyMetrics.prosodyParseStart
      console.log(`⏱️ Prosody解析時間: ${prosodyParseTime.toFixed(2)}ms`)
    } else {
      console.log('⏭️ Prosody評価をスキップ（コスト削減）')
      prosodyResult = null
    }
    */
    
    // ステップ1: 語オブジェクトの構造を確認（デバッグ用）
    try {
      if (!this._loggedWordKeys && words.length > 0) {
        console.log('🔎 Word keys (words[0]):', Object.keys(words[0]))
        if (words[0].PronunciationAssessment) {
          console.log('🔎 Word PronunciationAssessment keys:', Object.keys(words[0].PronunciationAssessment))
          console.log('🔎 Word PronunciationAssessment content:', words[0].PronunciationAssessment)
        }
        this._loggedWordKeys = true
      }
    } catch {}
    
    // 単語データを標準化（エラー種別、強勢、Monotone情報を含める）
    const standardizedWords = words.map(word => {
      // Azure SDKのJSONでは大文字小文字が混在する可能性があるため、両方をチェック
      const wordText = word.Word || word.word || ''
      
      // PronunciationAssessmentオブジェクト内を探索（音素と同様の構造の可能性）
      const wordPA = word.PronunciationAssessment || word.pronunciationAssessment || null
      const wordAccFromPA = wordPA ? (
        wordPA.AccuracyScore ??
        wordPA.accuracyScore ??
        wordPA.Accuracy ??
        wordPA.accuracy ??
        undefined
      ) : undefined
      
      const wordAccuracyScore = 
        wordAccFromPA ??
        word.AccuracyScore ??
        word.accuracyScore ??
        0
      const wordOffset = word.Offset || word.offset || 0
      const wordDuration = word.Duration || word.duration || 0
      const wordExpectedWord = word.ExpectedWord || word.expectedWord || wordText
      const wordSyllables = word.Syllables || word.syllables || []
      const wordPhonemes = word.Phonemes || word.phonemes || []
      
      // エラー種別判定
      const errorType = this.detectWordErrorType({
        word: wordText,
        expectedWord: wordExpectedWord,
        accuracyScore: wordAccuracyScore
      })
      
      // 強勢語判定
      const isStressed = wordSyllables.some(s => {
        const stress = s.Stress || s.stress
        return stress === 'primary' || stress === 'secondary'
      }) || false
      
      // Monotone判定（語単位ではprosodyResultから取得）
      // Prosody評価無効化により、常にfalse（将来的に復活可能）
      const isMonotone = false  // prosodyResult?.wordFeedbacks?.find(fb => fb.word === wordText)?.intonation?.isMonotone || false
      
      return {
        word: wordText,
        expectedWord: wordExpectedWord, // 期待語（置換の場合）
        score: Math.round(wordAccuracyScore),
        accuracy: wordAccuracyScore,
        errorType,
        offset: wordOffset,
        duration: wordDuration,
        syllables: wordSyllables,
        phonemes: wordPhonemes,
        isStressed,
        isMonotone
      }
    })
    
    // 脱落語（ゴースト）を抽出
    const missingWords = this.extractMissingWords(words, text)
    
    // レイテンシ計測完了
    this.latencyMetrics.parseEnd = performance.now()
    
    // レイテンシ結果をログ出力
    if (this.latencyMetrics.sdkResponseStart && this.latencyMetrics.parseStart) {
      const totalLatency = this.latencyMetrics.parseEnd - this.latencyMetrics.sdkResponseStart
      const parseLatency = this.latencyMetrics.parseEnd - this.latencyMetrics.parseStart
      console.log(`⏱️ レイテンシ計測結果:`)
      console.log(`   SDK応答 → 解析完了: ${totalLatency.toFixed(2)}ms`)
      console.log(`   解析処理時間: ${parseLatency.toFixed(2)}ms`)
      // Prosody解析時間のログ（コスト削減のため無効化中）
      /*
      if (this.latencyMetrics.prosodyParseStart && this.latencyMetrics.prosodyParseEnd) {
        const prosodyLatency = this.latencyMetrics.prosodyParseEnd - this.latencyMetrics.prosodyParseStart
        console.log(`   Prosody解析時間: ${prosodyLatency.toFixed(2)}ms (${(prosodyLatency / parseLatency * 100).toFixed(1)}%)`)
      }
      */
    }
    
    // 結果を返す
    return {
      // スコア
      overall: overall == null ? null : Math.round(overall),
      accuracy: accuracy == null ? null : Math.round(accuracy),
      fluency: fluency == null ? null : Math.round(fluency),
      completeness: completeness == null ? null : Math.round(completeness),
      // Prosodyスコア（コスト削減のため無効化中、将来的に復活可能）
      // Prosody評価は追加料金がかかるため、現在は0を返す
      prosody: 0,  // prosodyResult?.overall ?? Math.round((detailedScores.ProsodyAssessment?.ProsodyScore) ?? (detailedScores.PronunciationAssessment?.ProsodyScore) ?? 0),
      
      // 単語・音素データ
      words: standardizedWords,
      phonemes: this.parsePhonemeDetails(words, detailedScores), // 音素レベル詳細（IPA対応）
      missingWords, // 脱落語（ゴースト表示用）
      
      // Prosody詳細（コスト削減のため無効化中、将来的に復活可能）
      prosodyDetails: {
        breaks: [],  // prosodyResult?.wordFeedbacks?.flatMap(fb => fb.breaks) || [],
        stress: [],  // prosodyResult?.wordFeedbacks?.flatMap(fb => fb.syllables?.map(s => ({ offset: s.offset, duration: s.duration, level: s.stress })).flat() || [],
        monotone: [],  // prosodyResult?.wordFeedbacks?.filter(fb => fb.intonation?.isMonotone).map(fb => ({ start: fb.offset / 10000000, end: (fb.offset + fb.duration) / 10000000 })) || [],
        timelineData: []  // prosodyResult?.timelineData || []
      },
      
      // Prosody分析結果オブジェクト（詳細情報用、コスト削減のため無効化中）
      prosodyResult: null,  // prosodyResult
      
      ai_comment: this.generateAIComment(overall)
    }
  }
  
  /**
   * 音素レベル詳細スコア解析（IPA発音記号対応）
   */
  parsePhonemeDetails(words, detailedScores) {
    const phonemeDetails = []

    // ステップ1: 返却キーを一度だけログ（デバッグ用）
    try {
      if (!this._loggedPhonemeKeys) {
        const sampleWord = words?.find(w => Array.isArray(w.phonemes) && w.phonemes.length > 0)
        const samplePh = sampleWord?.phonemes?.[0]
        if (samplePh) {
          console.log('🔎 Phoneme keys (word.phonemes[0]):', Object.keys(samplePh))
        }
        const dsWord = detailedScores?.Words?.find(w => Array.isArray(w.Phonemes) && w.Phonemes.length > 0)
        const dsPh = dsWord?.Phonemes?.[0]
        if (dsPh) {
          console.log('🔎 Phoneme keys (privPronJson.Words[0].Phonemes[0]):', Object.keys(dsPh))
          if (dsPh.PronunciationAssessment) {
            console.log('🔎 PronunciationAssessment keys:', Object.keys(dsPh.PronunciationAssessment))
            console.log('🔎 PronunciationAssessment content:', dsPh.PronunciationAssessment)
          }
        }
        this._loggedPhonemeKeys = true
      }
    } catch {}

    // ステップ2: フォールバックを含めて音素情報を抽出
    for (const word of words) {
      if (word.phonemes && word.phonemes.length > 0) {
        for (const phoneme of word.phonemes) {
          const symbol = phoneme.Phoneme || phoneme.phoneme || phoneme.Symbol || phoneme.symbol
          const acc = 
            phoneme.AccuracyScore ??
            phoneme.accuracyScore ??
            phoneme.accuracy ??
            phoneme.Score ??
            phoneme.score ??
            undefined
          const offset = phoneme.Offset ?? phoneme.offset ?? null
          const duration = phoneme.Duration ?? phoneme.duration ?? null
          const qualityRaw = phoneme.Quality || phoneme.quality || null
          const quality = (typeof acc === 'number' && Number.isFinite(acc))
            ? this.getPhonemeQuality(acc)
            : (qualityRaw ? String(qualityRaw).toLowerCase() : this.getPhonemeQuality(undefined))
          
          phonemeDetails.push({
            phoneme: symbol,
            accuracy: typeof acc === 'number' ? acc : undefined,
            word: word.word || word.Word,
            position: offset,
            duration: duration,
            quality,
            expectedPhoneme: this.getExpectedPhoneme(symbol, (word.word || word.Word || ''))
          })
        }
      }
    }
    
    // 詳細スコアから音素情報を補完
    if (detailedScores.Words) {
      for (const wordDetail of detailedScores.Words) {
        if (wordDetail.Phonemes) {
          for (const phonemeDetail of wordDetail.Phonemes) {
            // 重複チェック
            const exists = phonemeDetails.some(p => 
              p.phoneme === phonemeDetail.Phoneme && p.word === wordDetail.Word
            )
            if (!exists) {
              // PronunciationAssessmentオブジェクト内を探索（iOS Safariで確認された構造）
              const pa = phonemeDetail.PronunciationAssessment || phonemeDetail.pronunciationAssessment || null
              const accFromPA = pa ? (
                pa.AccuracyScore ??
                pa.accuracyScore ??
                pa.Accuracy ??
                pa.accuracy ??
                undefined
              ) : undefined
              
              const acc = 
                accFromPA ??
                phonemeDetail.AccuracyScore ??
                phonemeDetail.accuracyScore ??
                phonemeDetail.accuracy ??
                phonemeDetail.Score ??
                phonemeDetail.score ??
                undefined
              
              const qualityRaw = (pa && (pa.Quality || pa.quality)) || phonemeDetail.Quality || phonemeDetail.quality || null
              
              phonemeDetails.push({
                phoneme: phonemeDetail.Phoneme || phonemeDetail.phoneme,
                accuracy: typeof acc === 'number' ? acc : undefined,
                word: wordDetail.Word || wordDetail.word,
                position: phonemeDetail.Offset ?? phonemeDetail.offset,
                duration: phonemeDetail.Duration ?? phonemeDetail.duration,
                quality: (typeof acc === 'number') ? this.getPhonemeQuality(acc) : (qualityRaw ? String(qualityRaw).toLowerCase() : this.getPhonemeQuality(undefined)),
                expectedPhoneme: this.getExpectedPhoneme((phonemeDetail.Phoneme || phonemeDetail.phoneme), (wordDetail.Word || wordDetail.word))
              })
            }
          }
        }
      }
    }
    
    console.log('🔍 音素詳細解析（IPA対応）:', phonemeDetails)
    return phonemeDetails
  }
  
  /**
   * 音素の質を判定
   */
  getPhonemeQuality(accuracyScore) {
    if (accuracyScore >= 90) return 'excellent'
    if (accuracyScore >= 80) return 'good'
    if (accuracyScore >= 70) return 'fair'
    if (accuracyScore >= 60) return 'poor'
    return 'very_poor'
  }
  
  /**
   * 期待される発音記号を取得（参考用）
   */
  getExpectedPhoneme(actualPhoneme, word) {
    // より詳細な音素辞書（CMUdict風）
    const phonemeDictionary = {
      // 子音
      'p': 'p', 'b': 'b', 't': 't', 'd': 'd', 'k': 'k', 'g': 'g',
      'f': 'f', 'v': 'v', 'θ': 'th', 'ð': 'th', 's': 's', 'z': 'z',
      'ʃ': 'sh', 'ʒ': 'zh', 'h': 'h', 'm': 'm', 'n': 'n', 'ŋ': 'ng',
      'l': 'l', 'r': 'r', 'w': 'w', 'j': 'y', 'tʃ': 'ch', 'dʒ': 'j',
      
      // 母音
      'i': 'ee', 'ɪ': 'i', 'e': 'e', 'ɛ': 'e', 'æ': 'a', 'ɑ': 'a',
      'ɔ': 'o', 'o': 'o', 'ʊ': 'u', 'u': 'oo', 'ʌ': 'u', 'ə': 'uh',
      
      // 二重母音
      'aɪ': 'ai', 'aʊ': 'au', 'oɪ': 'oi', 'eɪ': 'ay', 'oʊ': 'o'
    }
    
    // 単語別の期待音素（主要単語）
    const wordPhonemes = {
      'hello': ['h', 'ə', 'l', 'oʊ'],
      'world': ['w', 'ɜr', 'l', 'd'],
      'good': ['g', 'ʊ', 'd'],
      'morning': ['m', 'ɔr', 'n', 'ɪ', 'ŋ'],
      'thank': ['θ', 'æ', 'ŋ', 'k'],
      'you': ['j', 'u'],
      'very': ['v', 'ɛ', 'r', 'i'],
      'much': ['m', 'ʌ', 'tʃ'],
      'please': ['p', 'l', 'i', 'z'],
      'sorry': ['s', 'ɑ', 'r', 'i'],
      'excuse': ['ɪ', 'k', 's', 'k', 'j', 'u', 'z'],
      'me': ['m', 'i'],
      'yes': ['j', 'ɛ', 's'],
      'no': ['n', 'oʊ'],
      'help': ['h', 'ɛ', 'l', 'p'],
      'water': ['w', 'ɔ', 't', 'ər'],
      'food': ['f', 'u', 'd'],
      'time': ['t', 'aɪ', 'm'],
      'today': ['t', 'ə', 'd', 'eɪ'],
      'tomorrow': ['t', 'ə', 'm', 'ɑ', 'r', 'oʊ']
    }
    
    // 単語が辞書にある場合は、その単語の期待音素を返す
    if (wordPhonemes[word.toLowerCase()]) {
      const expectedPhonemes = wordPhonemes[word.toLowerCase()]
      const phonemeIndex = expectedPhonemes.indexOf(actualPhoneme)
      if (phonemeIndex !== -1) {
        return expectedPhonemes[phonemeIndex]
      }
    }
    
    // 一般的な音素マッピング
    return phonemeDictionary[actualPhoneme] || actualPhoneme
  }
  
  /**
   * リズム・Prosody分析（Break/Intonation/Syllable対応）
   */
  parseProsodyAnalysis(detailedScores, words = []) {
    // Prosody 詳細ブロック（Rhythm/Stress/Intonation）が無い環境がある。
    // その場合は「全体 ProsodyScore のみ」を返して graceful degrade する。
    let prosody = detailedScores.ProsodyAssessment || detailedScores.prosodyAssessment
    
    if (!prosody) {
      // ProsodyAssessment 詳細ブロックがない場合、PronunciationAssessment.ProsodyScore を確認
      const overallOnly = detailedScores.PronunciationAssessment?.ProsodyScore ?? 
                         detailedScores.PronunciationAssessment?.prosodyScore ?? null
      
      if (overallOnly == null) {
        console.warn('⚠️ Prosody 詳細もスコアも見つかりません:', detailedScores)
        return null
      }
      
      console.log('✅ ProsodyScoreのみ取得（詳細ブロックなし）:', overallOnly)
      
      // 最小限のProsodyオブジェクトを作成（全体スコアのみ）
      return {
        overall: Math.round(overallOnly),
        rhythm: null,
        intonation: null,
        stress: null,
        rhythmQuality: null,
        intonationQuality: null,
        wordFeedbacks: [],
        timelineData: []
      }
    }
    
    console.log('🔍 ProsodyAssessmentデータ:', prosody)
    
    const threshold = 0.75 // Break検知の推奨しきい値
    
    // 語単位のFeedback抽出（Break/Intonation）
    const wordFeedbacks = []
    if (words && words.length > 0) {
      for (const word of words) {
        // Azure SDKのJSONでは大文字小文字が混在する可能性があるため、両方をチェック
        const wordText = word.Word || word.word || ''
        const wordOffset = word.Offset || word.offset || 0
        const wordDuration = word.Duration || word.duration || 0
        const wordFeedback = word.Feedback || word.feedback || {}
        
        const feedback = {
          word: wordText,
          offset: wordOffset,
          duration: wordDuration,
          breaks: [],
          intonation: null,
          syllables: []
        }
        
        // Break分析（UnexpectedBreak/MissingBreak）
        const breakInfo = wordFeedback.Break || wordFeedback.break
        if (breakInfo) {
          const unexpectedBreak = breakInfo.UnexpectedBreak || breakInfo.unexpectedBreak
          const missingBreak = breakInfo.MissingBreak || breakInfo.missingBreak
          
          // UnexpectedBreak検知（信頼度>0.75）
          if (unexpectedBreak) {
            const confidence = unexpectedBreak.Confidence || unexpectedBreak.confidence || 0
            if (confidence > threshold) {
              feedback.breaks.push({
                type: 'unexpected',
                confidence: confidence,
                position: wordOffset
              })
            }
          }
          
          // MissingBreak検知（信頼度>0.75）
          if (missingBreak) {
            const confidence = missingBreak.Confidence || missingBreak.confidence || 0
            if (confidence > threshold) {
              feedback.breaks.push({
                type: 'missing',
                confidence: confidence,
                position: wordOffset
              })
            }
          }
        }
        
        // Intonation分析（Monotone検知）
        const intonation = wordFeedback.Intonation || wordFeedback.intonation
        if (intonation) {
          const monotone = intonation.Monotone || intonation.monotone
          if (monotone) {
            feedback.intonation = {
              isMonotone: true,
              confidence: monotone.Confidence || monotone.confidence || 0
            }
          }
        }
        
        // Syllable分析（en-USのみ、強勢可視化用）
        const syllables = word.Syllables || word.syllables || []
        if (syllables.length > 0) {
          feedback.syllables = syllables.map(syllable => ({
            stress: syllable.Stress || syllable.stress || 'none', // primary/secondary/none
            offset: syllable.Offset || syllable.offset || 0,
            duration: syllable.Duration || syllable.duration || 0,
            grapheme: syllable.Grapheme || syllable.grapheme || ''
          }))
        }
        
        wordFeedbacks.push(feedback)
      }
    }
    
    // Prosodyスコアの取得（大文字小文字両方に対応）
    const prosodyScore = prosody.ProsodyScore || prosody.prosodyScore || 0
    const rhythmScore = prosody.RhythmScore || prosody.rhythmScore || 0
    const intonationScore = prosody.IntonationScore || prosody.intonationScore || 0
    const stressScore = prosody.StressScore || prosody.stressScore || 0
    
    return {
      overall: prosodyScore,
      rhythm: rhythmScore,
      intonation: intonationScore,
      stress: stressScore,
      // リズムの質を判定
      rhythmQuality: this.getRhythmQuality(rhythmScore),
      // イントネーションの質を判定
      intonationQuality: this.getIntonationQuality(intonationScore),
      // Break/Intonation/Syllable詳細
      wordFeedbacks: wordFeedbacks,
      // タイムライン用データ
      timelineData: this.extractTimelineData(wordFeedbacks)
    }
  }
  
  /**
   * タイムライン可視化用データ抽出
   */
  extractTimelineData(wordFeedbacks) {
    const timeline = []
    for (const feedback of wordFeedbacks) {
      const startTime = feedback.offset / 10000000 // 秒に変換
      const endTime = (feedback.offset + feedback.duration) / 10000000
      
      timeline.push({
        word: feedback.word,
        start: startTime,
        end: endTime,
        duration: feedback.duration / 10000000,
        breaks: feedback.breaks,
        isMonotone: feedback.intonation?.isMonotone || false,
        syllables: feedback.syllables
      })
    }
    return timeline
  }
  
  /**
   * 単語のエラー種別を検出
   * @param {Object} word - Azure評価結果の単語データ
   * @returns {'substitution' | 'deletion' | 'insertion' | null}
   */
  detectWordErrorType(word) {
    // Azureの評価結果からエラー種別を判定
    // mispronunciationType があれば使用
    if (word.mispronunciationType) {
      const type = word.mispronunciationType.toLowerCase()
      if (type.includes('substitution') || type.includes('substitute')) {
        return 'substitution'
      }
      if (type.includes('deletion') || type.includes('delete')) {
        return 'deletion'
      }
      if (type.includes('insertion') || type.includes('insert')) {
        return 'insertion'
      }
    }
    
    // expectedWord と word.word が異なる場合は置換
    if (word.expectedWord && word.word && 
        word.expectedWord.toLowerCase() !== word.word.toLowerCase()) {
      return 'substitution'
    }
    
    // スコアが低い場合は置換の可能性が高い（簡易判定）
    if (word.accuracyScore < 70) {
      return 'substitution'
    }
    
    return null // エラーなし
  }
  
  /**
   * 脱落語（ゴースト）を抽出
   * @param {Array} words - 認識された単語配列
   * @param {string} referenceText - 参照文（期待される文）
   * @returns {Array} 脱落語の配列
   */
  extractMissingWords(words, referenceText) {
    if (!referenceText) return []
    
    // 参照文を単語に分割（アポストロフィ対応）
    const referenceWords = referenceText
      .toLowerCase()
      .replace(/['']/g, "'") // スマートクォート正規化
      .match(/[a-z]+(?:'[a-z]+)?/g) || []
    // Azure SDKのJSONでは大文字小文字が混在する可能性があるため、両方をチェック
    const recognizedWords = words.map(w => {
      const wordText = w.Word || w.word || ''
      return wordText.toLowerCase()
    }).filter(Boolean)
    
    const missingWords = []
    
    // 参照文の各単語が認識されているかチェック
    for (let i = 0; i < referenceWords.length; i++) {
      const refWord = referenceWords[i]
      
      // 完全一致または部分一致をチェック
      const found = recognizedWords.some(recWord => 
        recWord === refWord || recWord.includes(refWord) || refWord.includes(recWord)
      )
      
      if (!found) {
        // 直前の単語の位置を基準に推測（簡易版）
        const prevWord = i > 0 ? words.find(w => {
          const wordText = (w.Word || w.word || '').toLowerCase()
          return wordText === referenceWords[i - 1]?.toLowerCase()
        }) : null
        
        const prevWordOffset = prevWord ? (prevWord.Offset || prevWord.offset || 0) : 0
        const prevWordDuration = prevWord ? (prevWord.Duration || prevWord.duration || 0) : 0
        
        missingWords.push({
          expectedWord: refWord,
          word: refWord,
          confidence: 50, // デフォルト信頼度
          position: prevWordOffset + prevWordDuration
        })
      }
    }
    
    return missingWords
  }
  
  /**
   * リズムの質を判定
   */
  getRhythmQuality(rhythmScore) {
    if (rhythmScore >= 90) return 'natural'
    if (rhythmScore >= 80) return 'good'
    if (rhythmScore >= 70) return 'fair'
    if (rhythmScore >= 60) return 'mechanical'
    return 'unnatural'
  }
  
  /**
   * イントネーションの質を判定
   */
  getIntonationQuality(intonationScore) {
    if (intonationScore >= 90) return 'expressive'
    if (intonationScore >= 80) return 'clear'
    if (intonationScore >= 70) return 'adequate'
    if (intonationScore >= 60) return 'flat'
    return 'monotone'
  }
  
  /**
   * REST結果をパース
   */
  parseRESTResult(result) {
    return {
      overall: Math.round(result.overall),
      accuracy: Math.round(result.accuracy),
      fluency: Math.round(result.fluency),
      completeness: Math.round(result.completeness),
      words: result.words || [],
      ai_comment: result.ai_comment || this.generateAIComment(result.overall)
    }
  }
  
  /**
   * 無音PCMをPushStreamに書き込み開始（PLAYING中）
   */
  startSilencePusher() {
    if (this.silencePushTimer) {
      return  // 既に開始済み
    }
    
    if (!this.pushAudioStream) {
      console.warn('⚠️ PushStreamが初期化されていません')
      return
    }
    
    // 20msフレーム（16kHz）
    const frameMs = 20
    const samplesPerFrame = 16000 * frameMs / 1000  // 320サンプル
    const bytesPerFrame = samplesPerFrame * 2  // 640バイト（16bit = 2バイト）
    
    const pushSilenceFrame = () => {
      if (!this.pushAudioStream || this.isPlaying === false) {
        return  // 停止状態
      }
      
      // ゼロPCMを作成
      const silenceBytes = new Uint8Array(bytesPerFrame)
      try {
        this.pushAudioStream.write(silenceBytes)
      } catch (error) {
        console.error('⚠️ 無音pushエラー:', error)
        this.stopSilencePusher()
      }
    }
    
    // 初回即座に実行
    pushSilenceFrame()
    
    // 20ms間隔で定期実行
    this.silencePushTimer = setInterval(pushSilenceFrame, frameMs)
    
    console.log('🔇 無音push開始:', { frameMs, samplesPerFrame, bytesPerFrame })
  }
  
  /**
   * 無音push停止
   */
  stopSilencePusher() {
    if (this.silencePushTimer) {
      clearInterval(this.silencePushTimer)
      this.silencePushTimer = null
      console.log('🔇 無音push停止')
    }
  }
  
  /**
   * マイクPCMをPushStreamに書き込み
   * @param {Float32Array} float32Array - マイクからのFloat32データ
   */
  pushMicPcm(float32Array) {
    if (!this.pushAudioStream) {
      return
    }
    
    if (this.isPlaying) {
      return  // 再生中は無音pushに任せる
    }
    
    try {
      // ダウンサンプリング（必要時）
      const downsampled = this.downsampleTo16k(float32Array, this.inputSampleRate)
      
      // Float32 → Int16 PCM変換
      const pcm16 = this.floatTo16PCM(downsampled)
      
      // PushStreamに書き込み
      this.pushAudioStream.write(pcm16)
    } catch (error) {
      console.error('⚠️ マイクPCM pushエラー:', error)
    }
  }
  
  /**
 * iOS再生アンロック（ユーザー操作直後に呼ぶ）
 * - WebAudio を resume + 1フレームの無音再生
 * - HTMLAudio も 20ms の無音WAVを動的生成して prime
 *   → 巨大な data URI を排除
 * @returns {Promise<boolean>}
 */
async unlockIOSPlayback() {
  const isiOS = /iP(ad|hone|od)/.test(navigator.userAgent)
  if (!isiOS) { this._iosUnlocked = true; return true }
  if (this._iosUnlocked) return true

  try {
    // 1) WebAudio をアンロック
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (Ctx) {
      this._audioCtx ??= new Ctx()
      if (this._audioCtx.state === 'suspended') {
        await this._audioCtx.resume()
      }
      const buf = this._audioCtx.createBuffer(1, 1, this._audioCtx.sampleRate) // 1フレーム無音
      const src = this._audioCtx.createBufferSource()
      src.buffer = buf
      src.connect(this._audioCtx.destination)
      src.start(0)
    }

    // 2) HTMLAudio を prime（動的に超短無音WAVを生成）
    if (!this._refAudio) {
      this._refAudio = new Audio()
      this._refAudio.preload = 'auto'
      this._refAudio.setAttribute('playsinline', '')
      // 一部環境向け
      this._refAudio.playsInline = true
    }

    // 20ms の無音WAVを生成（44byteヘッダ + 0データ）
    const blob = (() => {
      const sampleRate = 44100, durationMs = 20, ch = 1
      const samples = Math.max(1, Math.floor(sampleRate * durationMs / 1000))
      const bytesPerSample = 2, blockAlign = ch * bytesPerSample
      const byteRate = sampleRate * blockAlign, dataSize = samples * blockAlign
      const buffer = new ArrayBuffer(44 + dataSize)
      const view = new DataView(buffer)
      let p = 0
      const w = s => { for (let i = 0; i < s.length; i++) view.setUint8(p++, s.charCodeAt(i)) }

      w('RIFF'); view.setUint32(4, 36 + dataSize, true)
      w('WAVE'); w('fmt '); view.setUint32(16, 16, true)
      view.setUint16(20, 1, true)              // PCM
      view.setUint16(22, ch, true)             // チャンネル
      view.setUint32(24, sampleRate, true)     // サンプルレート
      view.setUint32(28, byteRate, true)
      view.setUint16(32, blockAlign, true)
      view.setUint16(34, 16, true)             // 16bit
      w('data'); view.setUint32(40, dataSize, true)
      // データ部は 0 埋め（無音）
      return new Blob([buffer], { type: 'audio/wav' })
    })()

    const url = URL.createObjectURL(blob)
    try {
      this._refAudio.src = url
      this._refAudio.muted = true
      await this._refAudio.play()
      this._refAudio.pause()
      this._refAudio.muted = false
    } finally {
      URL.revokeObjectURL(url)
    }

    this._iosUnlocked = true
    return true
  } catch (e) {
    console.warn('iOS unlock failed:', e?.name, e?.message)
    return false
  }
}


  /**
   * 参照音声再生（R2の辞書MP3をダイレクト再生）
   * - 単語1語のみ対応。未登録ならコメントを返して何もしない。
   * - 文章は今後「文MP3」を登録後に対応予定。
   * @param {string} text - 再生したいテキスト（原則1語）
   * @param {string} [prefetchedUrl] - 事前取得済みURL（省略可、最適化用）
   * @param {string} [prefetchedWord] - 事前正規化済み単語（省略可、最適化用）
   * @returns {Promise<{ok: boolean, message?: string}>} 再生できた/できないの簡易コメント
   */
  async playReferenceAudio(text, prefetchedUrl = null, prefetchedWord = null) {
    // 事前正規化済み単語があれば使用、なければ正規化
    let word = prefetchedWord
    if (!word) {
      word = normalizeSingleWord(text)
    }
    
    if (!word) {
      return { ok: false, message: '辞書音声は現在「単語のみ」対応です。' }
    }

    // 事前取得済みURLがあれば使用、なければ取得
    let url = prefetchedUrl
    if (!url) {
      url = await getDictionaryAudioUrl(word)
      if (!url) {
        return { ok: false, message: `辞書音声が未登録です: "${word}"` }
      }
    }

    // Continuousセッション中なら無音pushで評価を維持
    // セッション開始前の再生では、この処理をスキップ
    if (this.pushAudioStream) {
      this.switchToPlaybackMode()
    }

    try {
      // 共有のAudio要素（iOS対策：ユーザー操作起点で呼ばれる前提）
      if (!this._refAudio) {
        this._refAudio = new Audio()
        this._refAudio.preload = 'auto'
        this._refAudio.setAttribute('playsinline', '')
      }
      
      // CORSヘッダがあるR2なら念のため
      this._refAudio.crossOrigin = 'anonymous'
      this._refAudio.src = url
      this._refAudio.currentTime = 0

      await this._refAudio.play()
      
      return await new Promise((resolve) => {
        const cleanup = () => {
          // 評価モードへ戻す
          if (this.pushAudioStream) this.switchToRecordingMode()
          // ハンドラ解除
          this._refAudio.onended = null
          this._refAudio.onerror = null
        }

        this._refAudio.onended = () => {
          cleanup()
          resolve({ ok: true })
        }

        this._refAudio.onerror = () => {
          cleanup()
          resolve({ ok: false, message: '参照音声の再生中にエラーが発生しました' })
        }
      })
    } catch (e) {
      if (this.pushAudioStream) this.switchToRecordingMode()
      
      // エラー理由を見分け
      if (e?.name === 'NotAllowedError') {
        return { ok: false, message: 'iOSでは再生にタップが必要です。▶︎ボタンから再生してください。' }
      }
      if (e?.name === 'NotSupportedError' || e?.name === 'DecodeError') {
        return { ok: false, message: '音声のデコードに失敗（形式/Content-Typeを確認）' }
      }
      return { ok: false, message: e?.message || '参照音声の再生に失敗しました' }
    }
  }
  
  /**
   * PushAudioInputStreamを使用した継続評価セッション開始
   * @param {string} referenceText - 評価対象テキスト
   * @param {Function} onRecognized - 認識完了コールバック（スコア更新用）
   * @param {Function} onVoiceActivity - 音声検知コールバック（無音タイマー延命用）
   * @returns {Promise<void>}
   */
  async startContinuousSession(referenceText, onRecognized = null, onVoiceActivity = null) {
    if (this.pushAudioStream) {
      console.warn('⚠️ 継続セッションは既に開始されています')
      return
    }
    
    try {
      // SDK をロード
      const speechsdk = await loadSpeechSDK()
      
      // 有効なトークンを確保
      const { token, region } = await this.ensureValidToken()
      
      // 現在の参照テキストを保存（クロージャ問題対策）
      this.currentReferenceText = referenceText
      
      // SpeechConfig設定
      this.speechConfig = speechsdk.SpeechConfig.fromAuthorizationToken(token, region)
      this.speechConfig.speechRecognitionLanguage = 'en-US'
      this.speechConfig.outputFormat = speechsdk.OutputFormat.Detailed
      this.speechConfig.setProperty(speechsdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, '5000')
      this.speechConfig.setProperty(speechsdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, '2000')
      // 本番では audioLogging は外す（レイテンシ＆コスト節約）
      // this.speechConfig.setProperty(speechsdk.PropertyId.SpeechServiceConnection_EnableAudioLogging, 'true')
      
      // PushAudioInputStream作成（16kHz/16bit/mono PCM）
      const format = speechsdk.AudioStreamFormat.getWaveFormatPCM(16000, 16, 1)
      this.pushAudioStream = speechsdk.AudioInputStream.createPushStream(format)
      
      // AudioConfig作成
      const audioConfig = speechsdk.AudioConfig.fromStreamInput(this.pushAudioStream)
      
      // Recognizer作成
      this.recognizer = new speechsdk.SpeechRecognizer(this.speechConfig, audioConfig)
      
      // PronunciationAssessmentConfig設定
      const pronunciationConfig = new speechsdk.PronunciationAssessmentConfig(
        referenceText,
        speechsdk.PronunciationAssessmentGradingSystem.HundredMark,
        speechsdk.PronunciationAssessmentGranularity.Phoneme
      )
      
      // Prosody評価設定（コスト削減のため無効化中、将来的に復活可能）
      // Prosody評価は追加料金がかかるため、現在は無効化
      /*
      try {
        if ('enableProsodyAssessment' in pronunciationConfig) {
          pronunciationConfig.enableProsodyAssessment = this.enableProsody
        }
        if ('phonemeAlphabet' in pronunciationConfig) {
          pronunciationConfig.phonemeAlphabet = "IPA"
        }
        if ('nbestPhonemeCount' in pronunciationConfig) {
          pronunciationConfig.nbestPhonemeCount = 1
        }
      } catch (error) {
        console.warn('⚠️ Prosody設定でエラー:', error)
      }
      */
      
      // IPA発音記号とNBest設定は保持（Prosody評価とは無関係）
      try {
        if ('phonemeAlphabet' in pronunciationConfig) {
          pronunciationConfig.phonemeAlphabet = "IPA"
        }
        if ('nbestPhonemeCount' in pronunciationConfig) {
          pronunciationConfig.nbestPhonemeCount = 1
        }
      } catch (error) {
        console.warn('⚠️ 設定でエラー:', error)
      }
      
      pronunciationConfig.applyTo(this.recognizer)
      
      // コールバック保存
      this.onSessionRecognized = onRecognized
      this.onVoiceActivity = onVoiceActivity
      
      // イベントハンドラ設定
      this.recognizer.recognizing = (sender, e) => {
        if (e.result.reason === speechsdk.ResultReason.RecognizingSpeech) {
          console.log('🔍 途中経過認識:', e.result.text)
        }
      }
      
      this.recognizer.recognized = (sender, e) => {
        try {
          // レイテンシ計測開始
          this.latencyMetrics.sdkResponseStart = performance.now()
          
          if (!e.result || e.result.reason !== speechsdk.ResultReason.RecognizedSpeech) {
            return
          }
          
          const recognizedText = e.result.text?.trim() || ''
          if (!recognizedText || recognizedText === '.' || recognizedText.length < 3) {
            return
          }
          
          // RAW JSON取得
          let rawJsonData = null
          try {
            const rawJson = e.result.properties.getProperty(
              speechsdk.PropertyId.SpeechServiceResponse_JsonResult
            )
            if (rawJson) {
              rawJsonData = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson
            }
          } catch (error) {
            console.warn('⚠️ RAW JSONパースエラー:', error)
          }
          
          // 結果パース（現在の参照テキストを使用）
          const pronunciationResult = speechsdk.PronunciationAssessmentResult.fromResult(e.result)
          const result = this.parseSDKResult(pronunciationResult, this.currentReferenceText, rawJsonData)
          
          console.log('✅ 継続セッション認識結果:', result)
          
          // コールバック呼び出し
          if (this.onSessionRecognized) {
            this.onSessionRecognized(result)
          }
        } catch (error) {
          console.error('❌ 認識結果処理エラー:', error)
        }
      }
      
      this.recognizer.canceled = (sender, e) => {
        console.error('❌ 継続セッションキャンセル:', e.reason, e.errorDetails)
        // 再接続処理はWordPracticeModalで実装
      }
      
      // AudioContext初期化（マイク取得用、既存があれば再利用）
      if (!this.audioContext || this.audioContext.state === 'closed') {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
      }
      
      // マイク取得（既存ストリームの再利用）
      if (!this.audioStream) {
        this.audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        })
      } else {
        // 既存ストリームの有効性をチェック
        const live = this.audioStream.getAudioTracks().some(track => track.readyState === 'live')
        if (!live) {
          // Soft endなら live のままなのでここに入らない＝再許可が出ない
          console.log('⚠️ 既存ストリームが無効、再取得します')
          this.audioStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: false,
              noiseSuppression: false,
              autoGainControl: false
            }
          })
        }
      }
      
      // 入力サンプルレートを取得
      const source = this.audioContext.createMediaStreamSource(this.audioStream)
      this.inputSampleRate = this.audioContext.sampleRate
      
      // ScriptProcessorNode作成（2048サンプルバッファ、レイテンシ削減）
      const bufferSize = 2048
      this.scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1)
      
      // ゼロゲイン経路を作成（ハウリング防止）
      const muteGain = this.audioContext.createGain()
      muteGain.gain.value = 0
      
      // マイクPCMを取得してPushStreamに書き込み
      this.scriptProcessor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0)
        this.pushMicPcm(inputData)
        
        // 音声検知（RMS計算）で無音タイマー延命
        const rms = this.calculateRMS(inputData)
        if (this.onVoiceActivity && rms > 0.01) {
          this.onVoiceActivity()
        }
      }
      
      // 接続（ゼロゲイン経路でハウリング防止）
      source.connect(this.scriptProcessor)
      this.scriptProcessor.connect(muteGain)
      muteGain.connect(this.audioContext.destination)
      this.sourceNode = source
      
      console.log('✅ 継続セッション初期化完了:', {
        inputSampleRate: this.inputSampleRate,
        bufferSize,
        referenceText
      })
      
      // 連続認識開始（Promiseラップで確実に待つ）
      await new Promise((resolve, reject) => {
        this.recognizer.startContinuousRecognitionAsync(resolve, reject)
      })
      console.log('✅ 継続セッション開始')
      
    } catch (error) {
      console.error('❌ 継続セッション開始エラー:', error)
      await this.endContinuousSession()
      throw error
    }
  }
  
  /**
   * 参照音再生モードに切り替え（無音push開始）
   */
  switchToPlaybackMode() {
    this.isPlaying = true
    this.startSilencePusher()
    console.log('🔊 再生モードに切り替え（無音push開始）')
  }
  
  /**
   * 録音モードに切り替え（無音push停止）
   */
  switchToRecordingMode() {
    this.isPlaying = false
    this.stopSilencePusher()
    console.log('🎤 録音モードに切り替え（無音push停止）')
  }
  
  /**
   * referenceTextを更新（語モーダル用）
   * @param {string} newText - 新しい評価テキスト
   */
  async updateReferenceText(newText) {
    if (!this.recognizer) {
      console.warn('⚠️ Recognizerが初期化されていません')
      return
    }
    
    const speechsdk = await loadSpeechSDK()
    
    // 現在の参照テキストを更新
    this.currentReferenceText = newText
    
    // 新しいPronunciationAssessmentConfig作成
    const pronunciationConfig = new speechsdk.PronunciationAssessmentConfig(
      newText,
      speechsdk.PronunciationAssessmentGradingSystem.HundredMark,
      speechsdk.PronunciationAssessmentGranularity.Phoneme
    )
    
    // Prosody設定（コスト削減のため無効化中、将来的に復活可能）
    // Prosody評価は追加料金がかかるため、現在は無効化
    /*
    try {
      if ('enableProsodyAssessment' in pronunciationConfig) {
        pronunciationConfig.enableProsodyAssessment = this.enableProsody
      }
      if ('phonemeAlphabet' in pronunciationConfig) {
        pronunciationConfig.phonemeAlphabet = "IPA"
      }
      if ('nbestPhonemeCount' in pronunciationConfig) {
        pronunciationConfig.nbestPhonemeCount = 1
      }
    } catch (error) {
      console.warn('⚠️ Prosody設定でエラー:', error)
    }
    */
    
    // IPA発音記号とNBest設定は保持（Prosody評価とは無関係）
    try {
      if ('phonemeAlphabet' in pronunciationConfig) {
        pronunciationConfig.phonemeAlphabet = "IPA"
      }
      if ('nbestPhonemeCount' in pronunciationConfig) {
        pronunciationConfig.nbestPhonemeCount = 1
      }
    } catch (error) {
      console.warn('⚠️ 設定でエラー:', error)
    }
    
    // 適用
    pronunciationConfig.applyTo(this.recognizer)
    
    console.log('✅ referenceText更新:', newText)
  }
  
  /**
   * 継続セッション終了
   * @param {Object} options - 終了オプション
   * @param {boolean} options.hard - true: 完全離脱（tracksをstop）、false: ソフト終了（tracksを保持）
   */
  async endContinuousSession({ hard = false } = {}) {
    console.log('🔚 継続セッション終了処理開始 (hard:', hard, ')')
    
    // 無音push停止
    this.stopSilencePusher()
    
    // Recognizer停止（Promiseラップで確実に待つ）
    if (this.recognizer) {
      try {
        await new Promise((resolve, reject) => {
          this.recognizer.stopContinuousRecognitionAsync(resolve, reject)
        })
      } catch (error) {
        console.warn('⚠️ Recognizer停止エラー:', error)
      }
      this.recognizer = null
    }
    
    // ScriptProcessor切断
    if (this.scriptProcessor) {
      try {
        this.scriptProcessor.disconnect()
      } catch (error) {
        console.warn('⚠️ ScriptProcessor切断エラー:', error)
      }
      this.scriptProcessor = null
    }
    
    // SourceNode切断
    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect()
      } catch (error) {
        console.warn('⚠️ SourceNode切断エラー:', error)
      }
      this.sourceNode = null
    }
    
    // AudioContext終了（Soft endではcloseしない）
    if (this.audioContext) {
      try {
        // Soft end では close しない（iOSでの再開が軽くなる）
        if (hard) {
          await this.audioContext.close()
        }
      } catch (error) {
        console.warn('⚠️ AudioContext終了エラー:', error)
      }
      if (hard) {
        this.audioContext = null
      }
    }
    
    // PushStream終了
    if (this.pushAudioStream) {
      try {
        this.pushAudioStream.close()
      } catch (error) {
        console.warn('⚠️ PushStream終了エラー:', error)
      }
      this.pushAudioStream = null
    }
    
    // マイクストリーム停止（Hard endのみ）
    if (hard) {
      // 完全離脱時のみ stop
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop())
        this.audioStream = null
      }
    } else {
      // Soft end: trackは生かす（権限維持のため）
      // 何もしない
    }
    
    // フラグリセット
    this.isPlaying = false
    this.onSessionRecognized = null
    this.onVoiceActivity = null
    this.currentReferenceText = ''
    
    console.log('✅ 継続セッション終了完了 (hard:', hard, ')')
  }
  
  /**
   * AIコメント生成
   */
  generateAIComment(score) {
    if (score >= 95) return '素晴らしい発音です！'
    if (score >= 85) return 'とても良い発音です'
    if (score >= 75) return '良い発音です'
    if (score >= 65) return 'もう少し練習しましょう'
    return '基礎から練習しましょう'
  }
  
  /**
   * 評価状態を取得
   */
  getEvaluationState() {
    return {
      mode: this.mode,
      isEvaluating: this.isEvaluating,
      hasConfig: !!this.speechConfig,
      hasRecognizer: !!this.recognizer
    }
  }
  
  /**
   * デバッグ用：評価状態をログ出力
   */
  logEvaluationState() {
    const state = this.getEvaluationState()
    console.log('🎯 Azure評価状態:', state)
    return state
  }
  
  /**
   * クリーンアップ（タイマー停止 + 継続セッション終了）
   */
  async cleanup() {
    // トークンリフレッシュタイマー停止
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer)
      this.tokenRefreshTimer = null
    }
    
    // 継続セッション終了（すべてのリソースをクリーンアップ、Hard end）
    await this.endContinuousSession({ hard: true })
    
    // 従来のevaluateWithSDKで使用していたリソースもクリーンアップ
    if (this.recognizer) {
      try {
        await new Promise((resolve, reject) => {
          this.recognizer.stopContinuousRecognitionAsync(resolve, reject)
        })
      } catch (error) {
        console.warn('⚠️ Recognizer停止エラー:', error)
      }
      this.recognizer = null
    }
    
    // MediaRecorderも停止
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop()
      } catch (error) {
        console.warn('⚠️ MediaRecorder停止エラー:', error)
      }
      this.mediaRecorder = null
    }
    this.recordingChunks = []
    
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop())
      this.audioStream = null
    }
    
    this.speechConfig = null
    this.isEvaluating = false
  }

    // ===== Sticky recognizer（先張り用）インスタンスAPI =====

  /** sticky認識器を確保（再利用 or 新規作成） */
  async ensureStickyReady(language = 'en-US') {
    const rec = await getOrCreateStickyRecognizer({ language })
    // 参照を保持（停止やイベント解除で使う）
    this._stickyRecognizer = rec
    return rec
  }

  /** sticky用の参照テキストを適用（毎回の単語/文に応じて差し替え） */
  async setStickyReferenceText(referenceText) {
    if (!referenceText) return
    if (!this._stickyRecognizer) await this.ensureStickyReady('en-US')
    // 既存のユーティリティをそのまま利用
    await applyPronAssessment(this._stickyRecognizer, referenceText, {
      granularity: 'Phoneme',
      enableProsody: false
    })
    this.currentReferenceText = referenceText
  }

  /**
   * sticky連続認識を開始
   * 使い方：
   *  - startContinuousRecognition('chocolate', onRecognizing, onRecognized)
   *  - startContinuousRecognition(onRecognizing, onRecognized) // 参照テキストは直前に setStickyReferenceText() 済み
   */
  async startContinuousRecognition(arg1, arg2, arg3) {
    let referenceText = null
    let onRecognizing = null
    let onRecognized = null

    // シグネチャ両対応
    if (typeof arg1 === 'string') {
      referenceText = arg1
      onRecognizing = arg2
      onRecognized = arg3
    } else {
      onRecognizing = arg1
      onRecognized = arg2
    }

    const rec = await this.ensureStickyReady('en-US')
    if (referenceText) {
      await this.setStickyReferenceText(referenceText)
    }

    // イベントハンドラ設定
    rec.recognizing = (_s, e) => {
      try { onRecognizing?.(e.result?.text || '') } catch {}
    }
    rec.recognized = (_s, e) => {
      try { onRecognized?.(e.result) } catch {}
    }

    // 連続認識開始（Promiseで待機して安全に開始）
    await new Promise((resolve, reject) => {
      rec.startContinuousRecognitionAsync(resolve, reject)
    })
    // 必要ならフラグ保持
    this._stickyRunning = true
  }

  /** sticky連続認識を停止（接続は維持しつつ停止） */
  async stopContinuousRecognition() {
    if (!this._stickyRecognizer) return
    try {
      await new Promise((resolve, reject) => {
        this._stickyRecognizer.stopContinuousRecognitionAsync(resolve, reject)
      })
    } catch (e) {
      console.warn('sticky stop error:', e?.message || e)
    } finally {
      this._stickyRunning = false
    }
  }


  /**
   * 単一MediaStreamを使用した1回限りの認識
   * @param {Object} params
   * @param {MediaStream} params.mediaStream - 共有するマイクストリーム
   * @param {string} params.referenceText - 評価対象テキスト
   * @param {string} [params.locale='en-US'] - ロケール
   * @param {boolean} [params.preconnect=true] - 事前接続の有無
   * @returns {Promise<{ recognizer: Object, wait: Function, close: Function, stopStream: Function }>}
   */
  async startPronOnceWithStream({ mediaStream, referenceText, locale = 'en-US', preconnect = true } = {}) {
    if (!mediaStream) {
      const err = new Error('MediaStreamが未取得のため録音できません')
      err.code = 'MICROPHONE_NOT_FOUND'
      err.errorType = 'MICROPHONE_ERROR'
      throw err
    }

    const hasLiveTrack = mediaStream.getAudioTracks().some(track => track.readyState === 'live')
    if (!hasLiveTrack) {
      const err = new Error('マイクが無効になっています。再度マイクの許可を確認してください。')
      err.code = 'MICROPHONE_NOT_FOUND'
      err.errorType = 'MICROPHONE_ERROR'
      throw err
    }

    const SDK = await loadSpeechSDK()
    const { token, region } = await this.ensureValidToken()

    const speechConfig = SDK.SpeechConfig.fromAuthorizationToken(token, region)
    speechConfig.speechRecognitionLanguage = locale
    speechConfig.outputFormat = SDK.OutputFormat.Detailed

    const format = SDK.AudioStreamFormat.getWaveFormatPCM(16000, 16, 1)
    const pushStream = SDK.AudioInputStream.createPushStream(format)
    const audioConfig = SDK.AudioConfig.fromStreamInput(pushStream)
    const recognizer = new SDK.SpeechRecognizer(speechConfig, audioConfig)
    await applyPronAssessment(recognizer, referenceText, { granularity: 'Phoneme', enableProsody: false })

    let connection = null
    if (preconnect && SDK.Connection && typeof SDK.Connection.fromRecognizer === 'function') {
      try {
        connection = SDK.Connection.fromRecognizer(recognizer)
        if (connection && typeof connection.openConnection === 'function') {
          connection.openConnection()
          console.log('✅ Azure接続（shared stream preconnect）成功')
        }
      } catch (error) {
        console.warn('⚠️ shared stream preconnect失敗（非致命）:', error?.message || error)
      }
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) {
      const err = new Error('このブラウザは AudioContext をサポートしていません')
      err.code = 'MICROPHONE_NOT_SUPPORTED'
      err.errorType = 'MICROPHONE_ERROR'
      throw err
    }

    const audioContext = new AudioContextClass()
    const sourceNode = audioContext.createMediaStreamSource(mediaStream)
    const bufferSize = 2048
    const processor = audioContext.createScriptProcessor(bufferSize, 1, 1)
    const muteGain = audioContext.createGain()
    muteGain.gain.value = 0
    const inputSampleRate = audioContext.sampleRate

    processor.onaudioprocess = (event) => {
      try {
        const inputData = event.inputBuffer.getChannelData(0)
        const downsampled = this.downsampleTo16k(inputData, inputSampleRate)
        const pcm16 = this.floatTo16PCM(downsampled)
        pushStream.write(pcm16)
      } catch (streamError) {
        console.error('⚠️ PushStream書き込みエラー:', streamError)
      }
    }

    sourceNode.connect(processor)
    processor.connect(muteGain)
    muteGain.connect(audioContext.destination)

    const wait = () => new Promise((resolve, reject) => {
      recognizer.recognizeOnceAsync(resolve, reject)
    })

    const stopStream = () => {
      try { processor.disconnect() } catch {}
      try { sourceNode.disconnect() } catch {}
      try { muteGain.disconnect() } catch {}
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch(() => {})
      }
    }

    const close = () => {
      try {
        if (connection && typeof connection.closeConnection === 'function') {
          connection.closeConnection()
        }
      } catch (e) {
        console.debug('⚠️ closeConnection失敗（無視）:', e?.message || e)
      }
      try {
        recognizer.close()
      } catch (e) {
        console.debug('⚠️ recognizer.close()失敗（無視）:', e?.message || e)
      }
      try {
        pushStream.close()
      } catch (e) {
        console.debug('⚠️ pushStream.close()失敗（無視）:', e?.message || e)
      }
    }

    return { recognizer, wait, close, stopStream }
  }
}

// ============================================
// Sticky Recognizer Utility (safe add-on)
// for WordPracticeModal only
// ============================================

import * as sdk from "microsoft-cognitiveservices-speech-sdk"

let stickyRecognizer = null
let stickySpeechConfig = null
let stickyAudioConfig = null
let stickyConnection = null
let stickyTokenInfo = { token: null, region: "japaneast", expiry: 0 }

/** 🔹 トークン取得（既存クラスのensureValidTokenを呼べないため独立） */
async function getAzureTokenDirect() {
  const { getApiBase } = await import('./apiBase.js')
  const { supabase } = await import('../supabaseClient.js')
  const session = await supabase.auth.getSession()
  const apiBase = getApiBase()
  const res = await fetch(`${apiBase}/api/azure/token`, {
    headers: { Authorization: `Bearer ${session.data.session?.access_token}` },
  })
  if (!res.ok) throw new Error(`Azure token fetch failed (${res.status})`)
  const data = await res.json()
  stickyTokenInfo = {
    token: data.token,
    region: data.region || "japaneast",
    expiry: Date.now() + (data.expires_in || 540) * 1000,
  }
  return stickyTokenInfo
}

/** 🔹 Sticky Recognizer生成または再利用（＋preconnect でWS先張り） */
export async function getOrCreateStickyRecognizer({ language = "en-US" } = {}) {
  // トークン有効期限チェック
  if (!stickyTokenInfo.token || Date.now() > stickyTokenInfo.expiry - 60_000) {
    await getAzureTokenDirect()
  }

  // 既存インスタンスを再利用
  if (stickyRecognizer) {
    console.log("♻️ Sticky recognizer 再利用中")
    return stickyRecognizer
  }

  const token = stickyTokenInfo.token
  const region = stickyTokenInfo.region
  console.log("🧠 Sticky recognizer 新規作成中...")

  stickySpeechConfig = sdk.SpeechConfig.fromAuthorizationToken(token, region)
  stickySpeechConfig.speechRecognitionLanguage = language
  stickySpeechConfig.outputFormat = sdk.OutputFormat.Detailed
  
  // 既存実装に合わせた推奨プロパティ
  try {
    stickySpeechConfig.setProperty?.(sdk.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, '5000')
    stickySpeechConfig.setProperty?.(sdk.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs, '2000')
  } catch {}

  // 1回だけマイクAudioConfig生成
  stickyAudioConfig = stickyAudioConfig || sdk.AudioConfig.fromDefaultMicrophoneInput()
  stickyRecognizer = new sdk.SpeechRecognizer(stickySpeechConfig, stickyAudioConfig)

  // 🔹 preconnect（接続だけ先に開く：openConnectionAsync ではなく openConnection）
  try {
    if (sdk.Connection && typeof sdk.Connection.fromRecognizer === "function") {
      stickyConnection = sdk.Connection.fromRecognizer(stickyRecognizer)
      if (stickyConnection && typeof stickyConnection.openConnection === "function") {
        stickyConnection.openConnection()
        console.log("✅ Sticky preconnect 成功")
      } else {
        console.debug("ℹ️ openConnection未対応。preconnectスキップ")
      }
    } else {
      console.debug("ℹ️ Connection.fromRecognizer未対応。preconnectスキップ")
    }
  } catch (e) {
    console.warn("⚠️ Sticky preconnect 失敗（非致命）:", e?.message || e)
  }

  console.log("✅ Sticky recognizer 初期化完了")
  return stickyRecognizer
}

/** 🔹 Sticky recognizer停止（接続維持） */
export async function pauseStickyRecognizer() {
  if (!stickyRecognizer) return
  try {
    await stickyRecognizer.stopContinuousRecognitionAsync()
    console.log("🛑 Sticky recognizer 一時停止（接続維持）")
  } catch (e) {
    console.warn("⚠️ Sticky停止エラー:", e)
  }
}

/** 🔹 Sticky recognizer完全破棄（preconnect WSも明示クローズ） */
export async function destroyStickyRecognizer() {
  // preconnectで開いたWSを先に閉じる（best-effort）
  try {
    stickyConnection?.closeConnection?.()
  } catch (e) {
    console.debug("⚠️ stickyConnection.closeConnection失敗（無視）:", e?.message || e)
  }
  stickyConnection = null

  if (stickyRecognizer) {
    try {
      await stickyRecognizer.close()
    } catch {}
    stickyRecognizer = null
  }

  stickySpeechConfig = null
  stickyAudioConfig = null
  console.log("🧹 Sticky recognizer 完全破棄")
}
